/* ═══════════════════════════════════════════════════════════════
   Editor Components — DocSettings, Library, Formatter, Settings
   Depends on: constants.ts, utils.ts
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import {
  Upload,
  Plus,
  Copy,
  X,
  Check,
  Play,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  FolderOpen,
  Loader2,
  History,
} from 'lucide-react';
import { FONTS } from '../constants';
import { tokLabel, tokPreview, tokModStyle } from '../utils';
import { isWeb } from '../lib/platform';
import { rem } from '../lib/rem';
import { toAddress } from '../routing/address';
import { getUiScale, setUserScale, resetUserScale, subscribeUiScale } from '../lib/uiScale';
import { usePref } from '../lib/usePref';
import { formatPercent } from '../../shared/uiScale';
import { Icon } from './ui/Icon';
import { AuthService } from '../services/authService';
import { SearchableSelect as SearchableSelectKV } from './SearchableSelect';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Hint } from './ui/tooltip';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { ToolbarButton } from '../editor-chrome/Toolbar';
import { FORMA_TIPS } from '../formaTooltips';
import { DataTable, Column, GroupOption } from './DataTable';
import {
  resolveOverwriteMode,
  originalsAtRisk,
  normPath,
  type OverwriteMode,
  type FormatConfirmation,
} from '../../shared/format-protection';
import {
  DirtyReviewDialog,
  keepDecisionFor,
  type DirtyDecision,
  type DirtyFileResult,
} from './DirtyReviewDialog';
import { countUndecided, toEngineDecisions } from './dirtyReaderSelection';
import { ConfirmDialog } from './ui/confirm-dialog';
import { hasPermission } from '../../shared/permissions';
import { activeOnly, isArchived } from '../lib/archive';
import { StatusBadge } from './StatusBadge';
import type { ArtifactScanEntry, FormatFileDirectives, FormatResult } from '../../shared/forma';
import { formatResultNote } from '../../shared/formatterRules';
import { isWordFile } from '../../shared/wordFiles';
import { fileBaseName, preflightFormatterInputs } from '../lib/formatterPreflight';
import { appLayerProps } from './ui/app-layer';

const { useState: eUseState, useEffect: eUseEffect, useRef: eUseRef } = React;

/* ─── Searchable Single-Select Dropdown ─── */
function SearchableSelect({
  value,
  options,
  placeholder,
  onSelect,
  inputStyle,
}: {
  value: string;
  options: string[];
  placeholder?: string;
  onSelect: (v: string) => void;
  inputStyle?: React.CSSProperties;
}) {
  const [open, setOpen] = eUseState(false);
  const [query, setQuery] = eUseState('');
  const ref = eUseRef<HTMLDivElement>(null);

  eUseEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => {
          setOpen(!open);
          setQuery('');
        }}
        style={{
          ...inputStyle,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'var(--space-6)',
        }}
      >
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: value ? 'var(--tx)' : 'var(--tm)',
          }}
        >
          {value || '— Select client —'}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
            marginLeft: 'var(--space-1)',
            flexShrink: 0,
          }}
        >
          {open ? '▲' : '▼'}
        </span>
      </div>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 200,
            marginTop: rem(2),
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,.15)',
            maxHeight: rem(220),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: `${rem(6)} var(--space-2)`, borderBottom: '1px solid var(--bd)' }}>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder || 'Search...'}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: `${rem(5)} var(--space-2)`,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ overflow: 'auto', flex: 1 }}>
            {value && (
              <div
                onClick={() => {
                  onSelect('');
                  setOpen(false);
                }}
                style={{
                  padding: `${rem(7)} var(--space-3)`,
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  color: 'var(--tm)',
                  cursor: 'pointer',
                  fontStyle: 'italic',
                  borderBottom: '1px solid var(--bd)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Clear selection
              </div>
            )}
            {filtered.length === 0 && (
              <div
                style={{
                  padding: 'var(--space-3)',
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  color: 'var(--tm)',
                  textAlign: 'center',
                }}
              >
                No matches
              </div>
            )}
            {filtered.map((o) => (
              <div
                key={o}
                onClick={() => {
                  onSelect(o);
                  setOpen(false);
                }}
                style={{
                  padding: `${rem(7)} var(--space-3)`,
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  cursor: 'pointer',
                  fontWeight: o === value ? 600 : 400,
                  color: o === value ? 'var(--ac)' : 'var(--tx)',
                  background: o === value ? 'rgba(var(--acr),0.06)' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (o !== value) e.currentTarget.style.background = 'var(--sa)';
                }}
                onMouseLeave={(e) => {
                  if (o !== value) e.currentTarget.style.background = 'transparent';
                }}
              >
                {o}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Doc Settings Panel ─── */
/* ─── Doc Settings building blocks ─── */

const DS_LABEL: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--tm)',
  letterSpacing: '0',
  display: 'block',
  marginBottom: rem(6),
};
const DS_INPUT: React.CSSProperties = {
  width: '100%',
  height: rem(36),
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  boxShadow: 'var(--shadow-sm)',
  boxSizing: 'border-box',
};

/** Settings group — "C hybrid": warm, soft, grouped (label above a rounded,
 *  borderless card) but every control stays inline-editable, no Apple-Settings
 *  drill-in. Reads like a modern canvas tool (Framer/Canva), not a form dump. */
function DSSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2.5 select-none pl-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-muted">
        {title}
      </div>
      <div className="sf-ds-card flex flex-col gap-3.5 rounded-lg border border-border bg-surface p-4 shadow-sm">
        {children}
      </div>
    </div>
  );
}

/** A settings label that explains itself on hover/focus (DESIGN.md §7: the
 *  label stays a real <label>, the tooltip only adds description). */
function DSLabel({
  hint,
  htmlFor,
  children,
}: {
  hint?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <Hint text={hint}>
      <label style={DS_LABEL} htmlFor={htmlFor} tabIndex={hint ? 0 : undefined}>
        {children}
      </label>
    </Hint>
  );
}

/** Compact toggle switch row. */
function DSToggle({
  on,
  label,
  hint,
  onToggle,
}: {
  on: boolean;
  label: string;
  hint?: string;
  onToggle: () => void;
}) {
  const id = React.useId();
  return (
    <div className="flex items-center justify-between gap-2 text-[12px]">
      <Hint text={hint}>
        <label htmlFor={id} className="cursor-pointer select-none">
          {label}
        </label>
      </Hint>
      <Switch id={id} checked={on} onCheckedChange={() => onToggle()} />
    </div>
  );
}

/** One margin field in the page diagram. */
function DSMarginInput({
  label,
  value,
  hint,
  onChange,
}: {
  label: string;
  value: number;
  hint?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: rem(2) }}>
      <Hint text={hint}>
        <span
          tabIndex={hint ? 0 : undefined}
          style={{
            fontSize: 'calc(var(--font-size-base) * 9 / 13)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--tm)',
            textTransform: 'uppercase',
            letterSpacing: '.05em',
          }}
        >
          {label}
        </span>
      </Hint>
      <Input
        type="number"
        step=".1"
        min="0"
        max="3"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="h-8 w-[52px] px-1 text-center font-mono text-[11px]"
      />
    </div>
  );
}

function DocSettings({
  doc,
  onUpdate,
  organizations,
  sections,
  pageVariant,
  onPageVariant,
}: {
  doc: any;
  onUpdate: (d: any) => void;
  organizations?: any[];
  /** When set, only render these DSSection titles (used to split Doc Settings
   *  across separate dockable tool palettes). Omit to render all. */
  sections?: string[];
  /** Which page variant the canvas is editing (first/odd/even) — the switcher
   *  renders inside Layout Options, next to the toggles that create variants. */
  pageVariant?: string;
  onPageVariant?: (v: string) => void;
}) {
  const u = (k: string, v: any) => onUpdate({ ...doc, [k]: v });
  const show = (t: string) => !sections || sections.includes(t);
  const pageVariants: string[] = [
    ...(doc.firstPageDifferent ? ['first'] : []),
    'odd',
    ...(doc.oddEvenDifferent ? ['even'] : []),
  ];

  const orgNames = organizations
    ? [...new Set(organizations.map((o) => o.name || o.shortName).filter(Boolean))]
    : [];

  return (
    <div
      className="sf-palette-flow"
      style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: rem(18) }}
    >
      {show('Identity') && (
        <DSSection title="Identity">
          <div>
            <DSLabel hint={FORMA_TIPS.formaName}>Forma Name</DSLabel>
            <Input value={doc.standardName} onChange={(e) => u('standardName', e.target.value)} />
          </div>
          <div>
            <DSLabel hint={FORMA_TIPS.formaClient}>Client</DSLabel>
            <SearchableSelect
              value={doc.clientName || ''}
              options={orgNames}
              placeholder="Search clients..."
              onSelect={(v) => u('clientName', v)}
              inputStyle={DS_INPUT}
            />
          </div>
        </DSSection>
      )}

      {show('Typography') && (
        <DSSection title="Typography">
          <div>
            <DSLabel hint={FORMA_TIPS.bodyFont}>Body Font</DSLabel>
            <SearchableSelectKV
              value={doc.bodyFont}
              options={FONTS.map((f) => ({ value: f, label: f }))}
              onChange={(v) => u('bodyFont', v)}
              placeholder="— Font —"
              searchPlaceholder="Search fonts…"
              clearable={false}
              style={DS_INPUT}
            />
          </div>
          <div style={{ display: 'flex', gap: rem(10) }}>
            <div style={{ flex: 1 }}>
              <DSLabel hint={FORMA_TIPS.bodyFontSize}>Size (pt)</DSLabel>
              <Input
                type="number"
                min="6"
                max="16"
                value={doc.bodyFontSize}
                onChange={(e) => u('bodyFontSize', Number(e.target.value) || 10)}
                className="text-center font-mono"
              />
            </div>
            <div style={{ flex: 1 }}>
              <DSLabel hint={FORMA_TIPS.lineSpacing}>Line Spacing</DSLabel>
              <Input
                type="number"
                min="1"
                max="3"
                step=".5"
                value={doc.lineSpacing}
                onChange={(e) => u('lineSpacing', Number(e.target.value) || 1)}
                className="text-center font-mono"
              />
            </div>
          </div>
        </DSSection>
      )}

      {show('Page Margins (inches)') && (
        <DSSection title="Page Margins (inches)">
          {/* Page diagram — inputs sit where the margins live */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gridTemplateRows: 'auto auto auto',
              gap: rem(6),
              alignItems: 'center',
              justifyItems: 'center',
            }}
          >
            <div />
            <DSMarginInput
              label="Top"
              hint={FORMA_TIPS.marginTop}
              value={doc.marginTop}
              onChange={(v) => u('marginTop', v)}
            />
            <div />
            <DSMarginInput
              label="Left"
              hint={FORMA_TIPS.marginLeft}
              value={doc.marginLeft}
              onChange={(v) => u('marginLeft', v)}
            />
            <div
              style={{
                width: rem(58),
                height: rem(74),
                borderRadius: rem(3),
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 4px rgba(0,0,0,.08)',
              }}
            >
              <div
                style={{
                  width: rem(36),
                  height: rem(52),
                  border: '1px dashed var(--bd)',
                  borderRadius: rem(2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'calc(var(--font-size-base) * 8 / 13)',
                  color: 'var(--tm)',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                body
              </div>
            </div>
            <DSMarginInput
              label="Right"
              hint={FORMA_TIPS.marginRight}
              value={doc.marginRight}
              onChange={(v) => u('marginRight', v)}
            />
            <div />
            <DSMarginInput
              label="Bottom"
              hint={FORMA_TIPS.marginBottom}
              value={doc.marginBottom}
              onChange={(v) => u('marginBottom', v)}
            />
            <div />
          </div>
        </DSSection>
      )}

      {show('Formats') && (
        <DSSection title="Formats">
          <div>
            <DSLabel hint={FORMA_TIPS.specNumberFormat}>Spec # Format</DSLabel>
            <Input
              value={doc.specNumberFormat || '## ## ##'}
              onChange={(e) => u('specNumberFormat', e.target.value)}
              className="font-mono"
            />
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
              Use # for digits. E.g. ## ## ##, ######, ##-##-##
            </span>
          </div>
          <div>
            <DSLabel hint={FORMA_TIPS.dateFormat}>Date Format</DSLabel>
            <SearchableSelectKV
              value={doc.dateFormat || 'M/d/yyyy'}
              options={[
                { value: 'M/d/yyyy', label: '1/5/2026' },
                { value: 'MM/dd/yyyy', label: '01/05/2026' },
                { value: 'yyyy/MM/dd', label: '2026/01/05' },
                { value: 'MMMM d, yyyy', label: 'January 5, 2026' },
                { value: 'MMM d, yyyy', label: 'Jan 5, 2026' },
                { value: 'd MMMM yyyy', label: '5 January 2026' },
                { value: 'MM-dd-yyyy', label: '01-05-2026' },
                { value: 'yyyy-MM-dd', label: '2026-01-05' },
              ]}
              onChange={(v) => u('dateFormat', v)}
              searchPlaceholder="Search formats…"
              clearable={false}
              style={DS_INPUT}
            />
          </div>
        </DSSection>
      )}

      {show('Layout Options') && (
        <DSSection title="Layout Options">
          {/* First-page and odd/even variants are MUTUALLY EXCLUSIVE — turning
              one on turns the other off (a forma edits one axis at a time). */}
          <DSToggle
            on={!!doc.firstPageDifferent}
            label="First page different"
            hint={FORMA_TIPS.firstPageDifferent}
            onToggle={() =>
              onUpdate({
                ...doc,
                firstPageDifferent: !doc.firstPageDifferent,
                ...(!doc.firstPageDifferent ? { oddEvenDifferent: false } : {}),
              })
            }
          />
          <DSToggle
            on={!!doc.oddEvenDifferent}
            label="Odd/even pages different"
            hint={FORMA_TIPS.oddEvenDifferent}
            onToggle={() =>
              onUpdate({
                ...doc,
                oddEvenDifferent: !doc.oddEvenDifferent,
                ...(!doc.oddEvenDifferent ? { firstPageDifferent: false } : {}),
              })
            }
          />
          <DSToggle
            on={!!doc.widowOrphan}
            label="Widow/orphan control"
            hint={FORMA_TIPS.widowOrphan}
            onToggle={() => u('widowOrphan', !doc.widowOrphan)}
          />
          {/* Which variant the canvas shows — lives here because the two
              toggles above are what create the variants. */}
          {onPageVariant && pageVariants.length > 1 && (
            <div>
              <DSLabel hint={FORMA_TIPS.pageVariantSwitcher}>Editing</DSLabel>
              <div style={{ display: 'flex', gap: rem(6), flexWrap: 'wrap' }}>
                {pageVariants.map((pvo) => {
                  const active = (pageVariant ?? 'odd') === pvo;
                  const label =
                    pvo === 'first'
                      ? 'First Page'
                      : pvo === 'even'
                        ? 'Even Pages'
                        : doc.oddEvenDifferent
                          ? 'Odd Pages'
                          : doc.firstPageDifferent
                            ? 'Other Pages'
                            : 'All Pages';
                  return (
                    <Button
                      key={pvo}
                      variant={active ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => onPageVariant(pvo)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </DSSection>
      )}
    </div>
  );
}

/* ─── Editor history: versions + edits, ONE trigger, ONE panel ───
   Versions and edits used to be two buttons (a "Versions" modal on one
   surface, this file's icon-only "Edit history" popover on the others).
   One History button now opens one portalled, viewport-clamped panel with
   both as kit Tabs. The Versions tab only exists when the caller HAS
   version data — its absence reads as "this surface has no versions" once,
   at the trigger, never as an empty tab. */

export interface EditorHistoryVersion {
  version: number;
  savedBy: string;
  savedAt: string;
  blockCount: number;
}

/** The narrowest shape the Edits tab actually reads. Deliberately looser
 *  than shared `EditHistoryEntry` (whose `action` is a closed union): some
 *  consumers reshape archived data into this same display shape without
 *  carrying that exact union, and the row rendering already falls back to
 *  "Edited" for anything it doesn't recognise. */
export interface EditorHistoryEntry {
  userName: string;
  action: string;
  timestamp: string;
  /** What happened, in words — shown instead of the action label. */
  note?: string;
}

function fmtHistoryTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
}

function historyActionLabel(a: string): string {
  return a === 'created'
    ? 'Created'
    : a === 'published'
      ? 'Published'
      : a === 'duplicated'
        ? 'Duplicated'
        : 'Edited';
}

function VersionsTab({
  versions,
  onRevert,
  canRevert,
}: {
  versions: EditorHistoryVersion[];
  onRevert?: (version: number) => void;
  canRevert?: boolean;
}) {
  if (!versions.length) {
    return (
      <div className="dsg-history-empty" role="status">
        No previous versions yet.
      </div>
    );
  }
  return (
    <>
      {versions.map((v) => (
        <div key={v.version} className="dsg-history-row">
          <div className="dsg-history-meta">
            <span className="dsg-history-time">v{v.version}</span>
            <span className="dsg-history-who">
              by {v.savedBy} —{' '}
              {new Date(v.savedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}{' '}
              · {v.blockCount} blocks
            </span>
          </div>
          <div className="dsg-history-actions">
            <button
              type="button"
              className="dsg-history-btn"
              disabled={!canRevert || !onRevert}
              onClick={() => onRevert?.(v.version)}
            >
              Revert
            </button>
          </div>
        </div>
      ))}
    </>
  );
}

function EditsTab({ entries }: { entries: EditorHistoryEntry[] }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="dsg-history-empty" role="status">
        No edit history yet.
      </div>
    );
  }
  return (
    <>
      {entries.map((e, i) => (
        <div key={i} className="dsg-history-row">
          <span className="dsg-history-avatar">
            {String(e.userName || '')
              .split(/\s+/)
              .map((w: string) => w[0]?.toUpperCase())
              .join('')
              .slice(0, 2)}
          </span>
          <div className="dsg-history-meta">
            <span className="dsg-history-time">{e.userName}</span>
            <span className="dsg-history-who">{e.note || historyActionLabel(e.action)}</span>
          </div>
          <span className="dsg-history-when">{fmtHistoryTime(e.timestamp)}</span>
        </div>
      ))}
    </>
  );
}

/** The merged History control. `versions` undefined → the Versions tab is
 *  hidden entirely (not shown empty); the panel is a single Edits list with
 *  its own heading, same as the old icon-only popover used to be. */
function EditorHistoryPanel({
  entries,
  versions,
  onRevert,
  canRevert = true,
}: {
  entries: EditorHistoryEntry[];
  versions?: EditorHistoryVersion[];
  onRevert?: (version: number) => void;
  canRevert?: boolean;
}) {
  const [anchor, setAnchor] = eUseState<{ left: number; bottom: number } | null>(null);
  const [tab, setTab] = eUseState<'versions' | 'edits'>(versions ? 'versions' : 'edits');
  const triggerRef = eUseRef<HTMLButtonElement>(null);
  const panelRef = eUseRef<HTMLDivElement>(null);

  eUseEffect(() => {
    if (!anchor) return;
    const onDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setAnchor(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setAnchor(null);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [anchor]);

  // Focus follows OPEN→CLOSE, never fires on mount: `anchor` starts null,
  // so a naive `else` branch here would steal focus onto the trigger on
  // every screen load. `wasOpenRef` only turns true once the panel has
  // actually opened, so the return-focus half only runs on a real close.
  const wasOpenRef = eUseRef(false);
  eUseEffect(() => {
    if (anchor) {
      wasOpenRef.current = true;
      panelRef.current?.querySelector<HTMLElement>('[role="tab"], button')?.focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [anchor]);

  const width = 320;
  const left = anchor ? Math.min(anchor.left, window.innerWidth - width - 8) : 0;
  const maxH = anchor ? Math.min(480, window.innerHeight - anchor.bottom - 16) : 0;

  return (
    <>
      <ToolbarButton
        ref={triggerRef}
        icon={History}
        label="History"
        aria-label="History — versions and edits"
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        active={!!anchor}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setAnchor((a) => (a ? null : { left: r.left, bottom: r.bottom }));
        }}
      />
      {anchor &&
        createPortal(
          <div
            {...appLayerProps}
            ref={panelRef}
            className="dsg-menu dsg-history-panel"
            role="dialog"
            aria-label="History"
            style={{ left, top: anchor.bottom + 6, width, maxHeight: maxH }}
          >
            {versions !== undefined ? (
              <Tabs value={tab} onValueChange={(v) => setTab(v as 'versions' | 'edits')}>
                <TabsList>
                  <TabsTrigger value="versions">Versions</TabsTrigger>
                  <TabsTrigger value="edits">Edits</TabsTrigger>
                </TabsList>
                <div className="dsg-history-panel-body">
                  {tab === 'versions' ? (
                    <VersionsTab
                      versions={versions}
                      // Revert opens its review on the canvas — the panel steps aside.
                      onRevert={
                        onRevert
                          ? (v) => {
                              setAnchor(null);
                              onRevert(v);
                            }
                          : undefined
                      }
                      canRevert={canRevert}
                    />
                  ) : (
                    <EditsTab entries={entries} />
                  )}
                </div>
              </Tabs>
            ) : (
              <div className="dsg-history-panel-body">
                <div className="dsg-history-head">Edit history</div>
                <EditsTab entries={entries} />
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

/* ─── Standards Library ─── */
/** Relative "when" for the library list (compact, quiet). */
function libTimeAgo(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
}

function Library({
  standards,
  defaultId,
  onLoad,
  onOpenClassic,
  onCreate,
  onDup,
  onDel,
  onArchive,
  onImport,
  userPermissions,
}: {
  standards: any[];
  defaultId: string;
  onLoad: (s: any) => void;
  /** Fall back to the classic (pre-Studio) master forma editor for this row —
   *  parity isn't proven yet, so this stays reachable (docs/DESIGNER_UNIFIED_PLAN.md §3 R6). */
  onOpenClassic?: (s: any) => void;
  onCreate: () => void;
  onImport: () => void;
  onDup: (id: string) => void;
  onDel: (id: string) => void;
  onArchive: (ids: string[], archived: boolean) => Promise<void>;
  userPermissions?: string[];
}) {
  const canImportTemplate = hasPermission(userPermissions, 'formas.import_template');
  const canArchive = hasPermission(userPermissions, 'formas.archive');
  const [showArchived, setShowArchived] = React.useState(false);
  const visible = React.useMemo(
    () => activeOnly(standards, showArchived),
    [standards, showArchived]
  );
  // Canonical table (DataTable) — the SAME list UX as Spec Masters / Projects /
  // Team etc.: sticky header, sort, search, group-by, selection, actions.
  const columns: Column<any>[] = [
    {
      key: 'name',
      label: 'Name',
      width: 220,
      getValue: (s) => s.docSettings?.standardName || 'Untitled',
      render: (s) => (
        <span className="inline-flex max-w-full items-center gap-2">
          <span className="truncate font-semibold text-text">
            {s.docSettings?.standardName || 'Untitled'}
          </span>
          {defaultId === s.id && (
            <span className="flex-shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-primary">
              Default
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'client',
      label: 'Client',
      width: 130,
      getValue: (s) => s.docSettings?.clientName || '',
      render: (s) => <span className="text-muted">{s.docSettings?.clientName || '—'}</span>,
    },
    {
      key: 'typeface',
      label: 'Typeface',
      width: 110,
      getValue: (s) =>
        `${s.docSettings?.bodyFont || 'Arial'} · ${s.docSettings?.bodyFontSize || 12}pt`,
      render: (s) => (
        <span className="text-muted">
          {s.docSettings?.bodyFont || 'Arial'} · {s.docSettings?.bodyFontSize || 12}pt
        </span>
      ),
    },
    {
      key: 'edited',
      label: 'Last edited',
      width: 120,
      getValue: (s) => s.editHistory?.[0]?.at || s.editHistory?.[0]?.timestamp || '',
      render: (s) => {
        const last = s.editHistory?.[0];
        if (!last) return <span className="text-muted">—</span>;
        return (
          <span className="text-muted">
            <span className="text-text">{last.userName || last.name || '—'}</span>
            <span className="mx-1.5 opacity-40">·</span>
            {libTimeAgo(last.at || last.timestamp)}
          </span>
        );
      },
    },
    {
      key: 'created',
      label: 'Created',
      width: 120,
      getValue: (s) => {
        const h = s.editHistory || [];
        const c = h.find((e: any) => e.action === 'created') || h[h.length - 1];
        return c?.at || c?.timestamp || '';
      },
      render: (s) => {
        const h = s.editHistory || [];
        const c = h.find((e: any) => e.action === 'created') || h[h.length - 1];
        if (!c) return <span className="text-muted">—</span>;
        return (
          <span className="text-muted">
            <span className="text-text">{c.userName || c.name || '—'}</span>
            <span className="mx-1.5 opacity-40">·</span>
            {libTimeAgo(c.at || c.timestamp)}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 136,
      minWidth: 128,
      maxWidth: 136,
      getValue: (s) => (s.archivedAt ? 'archived' : s.draftDirty ? 'pending_publish' : 'active'),
      render: (s) =>
        s.archivedAt ? (
          <StatusBadge status="archived" />
        ) : s.draftDirty ? (
          // The forma's shared draft holds unpublished edits (derived by the
          // server from the live document; the live-sync save refreshes it).
          <span
            title="Someone has unpublished edits in this forma"
            style={{ display: 'inline-flex' }}
          >
            <StatusBadge status="pending_publish" label="Pending publish" variant="info" />
          </span>
        ) : null,
    },
  ];

  const groupOptions: GroupOption<any>[] = [
    {
      key: 'client',
      label: 'By Client',
      getValue: (s) => s.docSettings?.clientName || 'No Client',
    },
    {
      key: 'typeface',
      label: 'By Typeface',
      getValue: (s) => s.docSettings?.bodyFont || 'Arial',
    },
  ];

  return (
    <div className="flex h-full w-full flex-col px-6 py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-[22px] font-bold leading-tight text-text">Master Formas</h2>
          <p className="m-0 mt-0.5 text-[13px] text-muted">
            Word formatting templates — one per client standard. Projects get their own copy on
            apply.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canImportTemplate && (
            <Button variant="outline" onClick={onImport}>
              <Icon icon={Upload} em={1.3636} strokeWidth={1.8} className="mr-1.5" />
              Import Word file
            </Button>
          )}
          <Button onClick={onCreate}>
            <Icon icon={Plus} em={1.3636} strokeWidth={2.2} className="mr-1.5" />
            New Forma
          </Button>
          <Button
            variant={showArchived ? 'secondary' : 'outline'}
            aria-pressed={showArchived}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DataTable<any>
          columns={columns}
          data={visible}
          keyField="id"
          onRowClick={(s) => onLoad(s)}
          rowHref={(s) => toAddress({ kind: 'forma-studio', formaId: s.id })}
          groupOptions={groupOptions}
          searchPlaceholder="Search formas…"
          initialSort={{ key: 'name', direction: 'asc' }}
          storageKey="formas-library-table-v2"
          emptyTitle="No saved formas"
          emptyMessage="Create a new forma or import a Word file (.docx or .dotx) to get started."
          permissions={userPermissions}
          bulkActions={{
            archive: {
              permission: 'formas.archive',
              onArchive: (items) =>
                onArchive(
                  items.map((s) => s.id),
                  true
                ),
              onUndo: (items) =>
                onArchive(
                  items.map((s) => s.id),
                  false
                ),
              label: 'Archive',
            },
            delete: {
              permission: 'formas.delete',
              onDelete: async (items) => {
                for (const s of items) await onDel(s.id);
              },
            },
            exportCsv: { permission: 'formas.export', filename: 'formas' },
            extra: (items, meta) => {
              const archivedOnes = items.filter((s) => s.archivedAt);
              const single = meta?.count === 1 ? items[0] : null;
              return (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      for (const s of items) onDup(s.id);
                      meta?.clear();
                    }}
                  >
                    <Icon icon={Copy} em={1.2} strokeWidth={1.8} className="mr-1.5" />
                    Duplicate
                  </Button>
                  {single && onOpenClassic && (
                    <Button variant="outline" size="sm" onClick={() => onOpenClassic(single)}>
                      <Icon icon={History} em={1.2} strokeWidth={1.8} className="mr-1.5" />
                      Open in classic editor
                    </Button>
                  )}
                  {archivedOnes.length > 0 && canArchive && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void onArchive(
                          archivedOnes.map((s) => s.id),
                          false
                        )
                      }
                    >
                      Restore {archivedOnes.length}
                    </Button>
                  )}
                </>
              );
            },
          }}
        />
      </div>
    </div>
  );
}

/* ─── Batch Formatter ─── */
function Formatter({
  standards,
  lastActiveId,
  settings,
  onSettingsChange,
  userPermissions,
  toast,
}: {
  standards: any[];
  lastActiveId: string | null;
  settings: any;
  onSettingsChange: (s: any) => void;
  userPermissions?: string[];
  /** The app toast — the report-write warning (D3) uses it. */
  toast?: (msg: string, isError?: boolean) => void;
}) {
  const canRun = hasPermission(userPermissions, 'formatter.run');
  const [sel, setSel] = eUseState(lastActiveId || settings?.defaultTemplateId || '');
  const [files, setFiles] = eUseState<string[]>([]);
  const [outputDir, setOutputDir] = eUseState(settings?.exportDir || '');
  // Optional: originals move into <archiveDir>/<YYYY.MM.DD_HHMM>/ before a run.
  const [archiveDir, setArchiveDir] = eUseState(settings?.archiveDir || '');
  const [running, setRunning] = eUseState(false);
  const [results, setResults] = eUseState<any[] | null>(null);
  // Full path of the batch's Formatting Report .docx (written to the output folder).
  const [reportPath, setReportPath] = eUseState<string | null>(null);
  // Folder(s) the originals were moved to in the last run (null = none moved).
  const [originalsMovedTo, setOriginalsMovedTo] = eUseState<string | null>(null);
  // Post-format review modal (Task 1): the LIVE review blocks report generation
  // until every flagged item is decided or dismissed. `savedDecisions` persists
  // per-file decisions for the report AND for a later read-only reopen.
  const [liveReview, setLiveReview] = eUseState<{ files: DirtyFileResult[] } | null>(null);
  const [liveDecisions, setLiveDecisions] = eUseState<
    Record<string, Record<string, DirtyDecision>>
  >({});
  const liveReviewResolveRef = eUseRef<
    ((d: Record<string, Record<string, DirtyDecision>>) => void) | null
  >(null);
  const [savedDecisions, setSavedDecisions] = eUseState<
    Record<string, Record<string, DirtyDecision>>
  >({});
  const [viewReview, setViewReview] = eUseState<{ files: DirtyFileResult[] } | null>(null);
  // T5 tracked-changes gate (formatting-artifacts R1): one all-or-nothing
  // Accept/Reject/Skip choice for every file in the batch that carries
  // unaccepted revisions, asked BEFORE the artifacts scan is even shown.
  const [revisionGate, setRevisionGate] = eUseState<{
    entries: ArtifactScanEntry[];
    onChoice: (choice: 'accept' | 'reject' | 'skip') => void;
    onCancel: () => void;
  } | null>(null);
  // "Reject all" inside the T5 gate is destructive (discards every other
  // author's Word edits, batch, no undo) — routed through the kit
  // ConfirmDialog before revisionGate.onChoice('reject') ever fires.
  const [revisionRejectConfirm, setRevisionRejectConfirm] = eUseState(false);
  // T4 artifacts prompt — Review vs Skip and format, once the pre-format
  // scan totals a nonzero artifact count.
  const [artifactsPrompt, setArtifactsPrompt] = eUseState<{
    totalArtifacts: number;
    fileCount: number;
    onReview: () => void;
    onSkip: () => void;
    onCancel: () => void;
  } | null>(null);
  // T7 "no silent shipping" — Done/Esc/backdrop on the review with undecided
  // items left asks this before the run is allowed to proceed.
  const [unresolvedConfirm, setUnresolvedConfirm] = eUseState<{
    count: number;
    resolve: (proceed: boolean) => void;
  } | null>(null);
  const [facets, setFacets] = eUseState<Record<string, boolean>>({
    headers: true,
    footers: true,
    prePost: true,
    body: true,
    pageSetup: true,
  });
  // Always-on pre-run confirmation gate — shown before EVERY batch run, not
  // just when conflicts exist (2026-08-14 "never again" fix). Cancel touches
  // nothing; conflicts.length === 0 still shows the summary before running.
  const [confirmModal, setConfirmModal] = eUseState<
    (FormatConfirmation & { onConfirm: (mode: OverwriteMode) => void; onCancel: () => void }) | null
  >(null);
  const [openAfterFormat, setOpenAfterFormat] = eUseState(settings?.openAfterFormat !== false);
  const [exportPdf, setExportPdf] = eUseState(false);
  const [pdfResults, setPdfResults] = eUseState<any[] | null>(null);
  // Cockpit functions — Format and Convert-to-PDF are independent, either/both can run.
  const [fnFormat, setFnFormat] = eUseState(true);
  const [dragActive, setDragActive] = eUseState(false);
  // Live run progress — which phase, how far along ("Formatting 3 of 12…").
  const [progress, setProgress] = eUseState<{
    phase: 'scan' | 'format' | 'pdf';
    done: number;
    total: number;
  } | null>(null);
  // Inline run warning — replaces the old native alert()s.
  const [runWarning, setRunWarning] = eUseState<string | null>(null);
  // Transient notice under the drop zone ("N non-Word files skipped").
  const [dropNotice, setDropNotice] = eUseState<string | null>(null);
  const dropNoticeTimer = eUseRef<ReturnType<typeof setTimeout> | null>(null);
  // Word COM availability — null while probing; false disables Convert to PDF.
  const [wordAvailable, setWordAvailable] = eUseState<boolean | null>(null);

  eUseEffect(() => {
    if (lastActiveId) setSel(lastActiveId);
  }, [lastActiveId]);

  // Probe for Microsoft Word once on mount (main caches the answer for the
  // session). If the probe itself is unavailable or errors, stay permissive —
  // the conversion path reports its own failure with a friendly message.
  eUseEffect(() => {
    let alive = true;
    const api = (window as any).api;
    if (api?.checkWordAvailable) {
      api
        .checkWordAvailable()
        .then((ok: boolean) => {
          if (alive) setWordAvailable(!!ok);
        })
        .catch(() => {
          if (alive) setWordAvailable(true);
        });
    } else {
      setWordAvailable(true);
    }
    return () => {
      alive = false;
    };
  }, []);

  eUseEffect(() => {
    return () => {
      if (dropNoticeTimer.current) clearTimeout(dropNoticeTimer.current);
    };
  }, []);

  // If the probe comes back "no Word" while Convert-to-PDF was already on,
  // switch it off — the disabled toggle explains why.
  eUseEffect(() => {
    if (wordAvailable === false) setExportPdf(false);
  }, [wordAvailable]);

  const togFacet = (k: string) => setFacets((p) => ({ ...p, [k]: !p[k] }));
  const anyFacet = Object.values(facets).some(Boolean);
  const pdfBlocked = wordAvailable === false;

  // Why can't Run run? Listed inline so the disabled button explains itself.
  const runBlockers: string[] = [];
  if (!files.length) runBlockers.push('Add at least one Word file');
  if (fnFormat && !sel) runBlockers.push('Select a forma');
  if (!outputDir) runBlockers.push('Choose an output folder');
  if (!fnFormat && !exportPdf)
    runBlockers.push('Turn on at least one function — Format or Convert to PDF');
  const runDisabled = running || runBlockers.length > 0 || (fnFormat && !anyFacet) || !canRun;

  /** Translate raw engine errors into plain English; raw text stays in the tooltip. */
  function friendlyError(raw: string | undefined, verb: 'Formatting' | 'PDF conversion'): string {
    const r = raw || '';
    if (/end of central directory|is this a zip file|corrupted zip|invalid zip/i.test(r))
      return "This file isn't a valid Word document (.docx).";
    if (
      /ENOENT|no such file or directory|input file not found|cannot find the (file|path)/i.test(r)
    )
      return 'File not found — it may have been moved or renamed.';
    if (/EBUSY|EPERM|EACCES|permission denied|being used by another process|locked/i.test(r))
      return 'The file is locked or in use — close it in Word and try again.';
    if (/word is not installed|word\.application|not accessible/i.test(r))
      return 'Microsoft Word is not installed or not accessible.';
    const short = r.split('\n')[0].trim().slice(0, 120);
    return short ? `${verb} failed — ${short}` : `${verb} failed.`;
  }

  function setOutputDirAndSave(dir: string) {
    setOutputDir(dir);
    if (onSettingsChange) onSettingsChange({ ...settings, exportDir: dir });
  }

  function setOpenAfterFormatAndSave(v: boolean) {
    setOpenAfterFormat(v);
    if (onSettingsChange) onSettingsChange({ ...settings, openAfterFormat: v });
  }

  function setArchiveDirAndSave(dir: string) {
    setArchiveDir(dir);
    if (onSettingsChange) onSettingsChange({ ...settings, archiveDir: dir });
  }

  /** Move originals into a dated archive folder; the run then formats FROM the
      archive (defuses the source-folder-as-output trap). Fail CLOSED: on any
      archive error the run is aborted and the selection restored. */
  async function archiveIfConfigured(
    jobFiles: string[]
  ): Promise<{ paths: string[]; folder: string | null } | null> {
    if (!archiveDir) return { paths: jobFiles, folder: null };
    try {
      const res = await (window as any).api.archiveOriginals(jobFiles, archiveDir);
      return { paths: res.archivedPaths, folder: res.archiveFolder };
    } catch (e: any) {
      setRunWarning(e?.message || String(e));
      setFiles(jobFiles); // restore the selection the reset already cleared
      return null;
    }
  }

  /** Archive rule keyed to the resolved write mode (Josh 2026-08-24 —
      "overwriting an original without a copy becomes impossible"):
        'rename'    → today's behavior: archive only if archiveDir is set.
        'overwrite' → archive is MANDATORY for originals at risk. Uses the
                       configured archiveDir when set; otherwise auto-archives
                       ONLY the at-risk files (Tweaks 33) beside them, one
                       IPC call per source folder (each lands in
                       "<folder>\_originals\<timestamp>\"), merged back in the
                       original file order. Fails CLOSED like archiveIfConfigured. */
  async function archiveForRun(
    jobFiles: string[],
    mode: OverwriteMode,
    atRisk: string[] = []
  ): Promise<{ paths: string[]; folder: string | null } | null> {
    if (mode !== 'overwrite' || archiveDir) return archiveIfConfigured(jobFiles);
    // Tweaks 33: the _originals fallback moves ONLY originals an Overwrite
    // could really clobber (a .docx output path equal to an input path).
    const riskSet = new Set(atRisk.map(normPath));
    const moving = jobFiles.filter((f) => riskSet.has(normPath(f)));
    if (moving.length === 0) return { paths: jobFiles, folder: null };
    const dirOf = (p: string) => p.replace(/[\\/][^\\/]*$/, '') || p;
    const groups = new Map<string, string[]>();
    for (const f of moving) {
      const d = dirOf(f);
      if (!groups.has(d)) groups.set(d, []);
      groups.get(d)!.push(f);
    }
    const resolvedByOriginal = new Map<string, string>();
    const folders: string[] = [];
    try {
      for (const [dir, group] of groups) {
        const dest = `${dir}\\_originals`;
        const res = await (window as any).api.archiveOriginals(group, dest);
        folders.push(res.archiveFolder);
        group.forEach((orig, i) => resolvedByOriginal.set(orig, res.archivedPaths[i]));
      }
      return {
        paths: jobFiles.map((f) => resolvedByOriginal.get(f) ?? f),
        folder: folders.join('; '),
      };
    } catch (e: any) {
      setRunWarning(e?.message || String(e));
      setFiles(jobFiles); // restore the selection the reset already cleared
      return null;
    }
  }

  /** Opens the LIVE review (pre-format — `filesForReview` carry the scan's
      artifacts, never an output) and resolves once the review is actually
      allowed to close. doFormat awaits this before formatting a single
      byte. */
  function reviewDirtyItems(
    filesForReview: DirtyFileResult[]
  ): Promise<Record<string, Record<string, DirtyDecision>>> {
    return new Promise((resolve) => {
      setLiveDecisions({});
      setLiveReview({ files: filesForReview });
      liveReviewResolveRef.current = resolve;
    });
  }

  function handleLiveDecide(file: string, id: string, decision: DirtyDecision) {
    setLiveDecisions((prev) => ({
      ...prev,
      [file]: { ...prev[file], [id]: decision },
    }));
  }

  /** "Keep all remaining": every undecided REVIEWABLE item takes an explicit
      Keep decision — never a silent no-op. The report lists each one as
      "Kept", same as a hand-picked Keep. Every item is a question
      (D6, 2026-10-13), so every undecided one is kept. */
  function handleKeepAllRemaining() {
    if (!liveReview) return;
    setLiveDecisions((prev) => {
      const next: Record<string, Record<string, DirtyDecision>> = { ...prev };
      for (const f of liveReview.files) {
        const fileDecisions = { ...(next[f.file] || {}) };
        for (const s of f.unheadered?.samples || []) {
          if (s.id && !fileDecisions[s.id]) fileDecisions[s.id] = keepDecisionFor();
        }
        next[f.file] = fileDecisions;
      }
      return next;
    });
  }

  /** Done, Esc, or backdrop dismiss on the LIVE review all route here (T7 —
      "no silent shipping"). Anything still undecided stops the close and
      asks first; only a confirmed "ship anyway" (or a review with nothing
      left undecided) actually lets the run proceed. Read-only reopen never
      calls this — it closes immediately (see the second dialog instance
      below). */
  async function handleReviewClose() {
    if (!liveReview) return;
    const undecided = liveReview.files.reduce(
      (n, f) => n + countUndecided(f.unheadered?.samples || [], liveDecisions[f.file] || {}),
      0
    );
    if (undecided > 0) {
      const proceed = await new Promise<boolean>((resolve) => {
        setUnresolvedConfirm({ count: undecided, resolve });
      });
      if (!proceed) return; // stays open — back to review
    }
    finishLiveReview();
  }

  function finishLiveReview() {
    const resolve = liveReviewResolveRef.current;
    liveReviewResolveRef.current = null;
    setLiveReview(null);
    resolve?.(liveDecisions);
  }

  /** Read-only reopen from a file's "Dirty spec" badge — shows what was
      decided (or the auto-fixed info) with no editing controls. */
  function openReadOnlyReview(r: any) {
    // outputPath lets the read-only reopen render the document reader too;
    // without it (older result rows) the modal falls back to the list.
    setViewReview({
      files: [{ file: r.file, outputPath: r.outputPath, unheadered: r.unheadered }],
    });
  }

  /** Append new paths, deduped case-insensitively (Windows paths). */
  function addFiles(paths: string[]) {
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => p.toLowerCase()));
      const next = [...prev];
      for (const p of paths) {
        const key = p.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          next.push(p);
        }
      }
      return next;
    });
  }

  function showDropNotice(msg: string) {
    setDropNotice(msg);
    if (dropNoticeTimer.current) clearTimeout(dropNoticeTimer.current);
    dropNoticeTimer.current = setTimeout(() => setDropNotice(null), 5000);
  }

  async function pickFiles() {
    if (!(window as any).api) return;
    const paths = await (window as any).api.openDocxDialog();
    if (paths && paths.length) addFiles(paths);
  }

  async function pickOutputDir() {
    if (!(window as any).api) return;
    const dir = await (window as any).api.selectFolderDialog();
    if (dir) setOutputDirAndSave(dir);
  }

  async function pickArchiveDir() {
    if (!(window as any).api) return;
    const dir = await (window as any).api.selectFolderDialog();
    if (dir) setArchiveDirAndSave(dir);
  }

  // Confirmation-gate contract (2026-08-21): the modal appears only when the
  // run could ACTUALLY clobber something.
  //   • Real conflict on disk  → modal, listing only the real conflicts
  //                              (same options and wording as before).
  //   • Archive-originals ON   → an "existing file" that IS one of the inputs
  //                              about to be MOVED into the archive is a
  //                              FALSE positive: archiveIfConfigured runs
  //                              before any output is written, so that file
  //                              is gone by then. Subtracted.
  //   • Conflict check ERRORED → still fails CLOSED (2026-08-14 "never
  //                              again"): the modal opens with a can't-verify
  //                              warning and the Overwrite option withheld,
  //                              so the only way forward is 'rename', which
  //                              never replaces a file.
  //   • Zero real conflicts    → no modal; the run proceeds in the safe
  //                              'rename' default (a no-op when nothing
  //                              collides).
  // handleFormat never writes a file directly; only doFormat does.
  async function handleFormat() {
    if (!sel) return setRunWarning('Select a forma first.');
    if (!files.length) return setRunWarning('Add at least one Word file first.');
    if (!outputDir) return setRunWarning('Choose an output folder first.');
    if (!anyFacet) return setRunWarning('Turn on at least one part to format.');
    const config = standards.find((s) => s.id === sel);
    if (!config) return setRunWarning('Forma not found — pick another one.');

    const conflicts: string[] = [];
    // Fail CLOSED: an error checking for existing files is NEVER read as "no
    // conflicts, proceed" — it forces the modal with Overwrite withheld.
    let checkFailed: string | null = null;
    if ((window as any).api?.checkFilesExist) {
      try {
        const docxConflicts = await (window as any).api.checkFilesExist(files, outputDir);
        if (docxConflicts?.length) conflicts.push(...docxConflicts);
      } catch (e: any) {
        checkFailed = `Couldn't check the output folder for existing files (${e?.message || String(e)}).`;
      }
    }
    // PDF outputs collide too when Convert-to-PDF is on — same modal, same choice.
    if (exportPdf && (window as any).api?.checkPdfConflicts) {
      try {
        const { conflicts: pdfConflicts } = await (window as any).api.checkPdfConflicts(
          files,
          outputDir
        );
        if (pdfConflicts?.length) conflicts.push(...pdfConflicts);
      } catch (e: any) {
        checkFailed =
          checkFailed ||
          `Couldn't check the output folder for existing PDF files (${e?.message || String(e)}).`;
      }
    }

    // Archiving moves every original OUT of its source folder before a single
    // output is written, so an "existing file" that is itself one of those
    // originals cannot be overwritten — subtract it (Windows paths: compare
    // case-insensitively with separators normalized).
    const inputPaths = new Set(files.map(normPath));
    const realConflicts = archiveDir
      ? conflicts.filter((c) => !inputPaths.has(normPath(c)))
      : conflicts;

    if (!checkFailed && realConflicts.length === 0) {
      // Nothing on disk can be clobbered — run straight through in the safe
      // 'rename' default rather than asking a question with one answer.
      doFormat(config, 'rename');
      return;
    }

    const atRisk = archiveDir ? [] : originalsAtRisk(files, realConflicts);
    const dirOfPath = (p: string) => p.replace(/[\\/][^\\/]*$/, '') || p;
    setConfirmModal({
      fileCount: files.length,
      filePaths: [...files],
      formaName: config.docSettings?.standardName || 'Untitled',
      outputDir,
      archiveDir: archiveDir || undefined,
      moveOriginals:
        atRisk.length > 0 && !checkFailed
          ? {
              files: atRisk,
              folders: [...new Set(atRisk.map((f) => `${dirOfPath(f)}\\_originals`))],
            }
          : undefined,
      conflicts: realConflicts.map((outputPath) => ({
        file: outputPath.split(/[/\\]/).pop() || outputPath,
        outputPath,
      })),
      checkFailed: checkFailed || undefined,
      onConfirm: (mode: OverwriteMode) => {
        setConfirmModal(null);
        doFormat(config, mode, atRisk);
      },
      onCancel: () => setConfirmModal(null),
    });
  }

  /** T5 gate: one all-or-nothing Accept/Reject/Skip choice for every file in
      `entries` (all `hasTrackedChanges`). Resolves 'cancel' on the dialog's
      Cancel — the whole run aborts, nothing touched. */
  function waitRevisionGate(
    entries: ArtifactScanEntry[]
  ): Promise<'accept' | 'reject' | 'skip' | 'cancel'> {
    return new Promise((resolve) => {
      setRevisionGate({
        entries,
        onChoice: (choice) => {
          setRevisionGate(null);
          resolve(choice);
        },
        onCancel: () => {
          setRevisionGate(null);
          resolve('cancel');
        },
      });
    });
  }

  /** T4 prompt: Review the scan's findings, or Skip and format as-is. */
  function waitArtifactsPrompt(
    totalArtifacts: number,
    fileCount: number
  ): Promise<'review' | 'skip' | 'cancel'> {
    return new Promise((resolve) => {
      setArtifactsPrompt({
        totalArtifacts,
        fileCount,
        onReview: () => {
          setArtifactsPrompt(null);
          resolve('review');
        },
        onSkip: () => {
          setArtifactsPrompt(null);
          resolve('skip');
        },
        onCancel: () => {
          setArtifactsPrompt(null);
          resolve('cancel');
        },
      });
    });
  }

  /** D3: a report that could not be written is a warning, never a swallow. */
  function reportWriteFailed(err: unknown) {
    const reason = err instanceof Error ? err.message : String(err);
    const msg = `Report not written: ${reason}`;
    if (toast) toast(msg, true);
    else setRunWarning(msg);
  }

  /** Every converted legacy file's temp .docx (D1) is removed after the run,
      success or failure — the run body records them in `convertedRunPaths`. */
  async function doFormat(config: any, overwriteModeIn: OverwriteMode, atRisk: string[] = []) {
    const convertedRunPaths: string[] = [];
    try {
      await runFormat(config, overwriteModeIn, convertedRunPaths, atRisk);
    } finally {
      const cleanup = window.api?.cleanupConvertedDocx;
      if (cleanup) {
        await Promise.all(
          convertedRunPaths.map((p) =>
            cleanup(p).catch((err: unknown) =>
              console.warn('[formatter] converted temp file not cleaned up:', p, err)
            )
          )
        );
      }
    }
  }

  async function runFormat(
    config: any,
    overwriteModeIn: OverwriteMode,
    convertedRunPaths: string[],
    atRisk: string[] = []
  ) {
    // Defense in depth: doFormat only ever runs from a modal confirmation
    // above, but never trust an upstream value blindly — resolve it through
    // the same safe-default rule as everywhere else.
    const overwriteMode = resolveOverwriteMode(overwriteModeIn);
    setRunning(true);
    setRunWarning(null);
    setResults(null);
    setPdfResults(null);
    setReportPath(null);
    setOriginalsMovedTo(null);
    // Every actual run resets the loaded documents (Josh 2026-08-20) — the
    // batch continues from originalJobFiles. Any abort below restores it.
    const originalJobFiles = [...files];
    setFiles([]);
    const api = (window as any).api;
    const safeConfig = JSON.parse(JSON.stringify(config));
    const abort = () => {
      setProgress(null);
      setRunning(false);
      setFiles(originalJobFiles);
    };

    // Body unchecked (D5): no scan, no review, no tracked-changes question —
    // the body is left byte-identical.
    const bodyOn = facets.body !== false;

    // ── 1b. PREFLIGHT (D1 + D3) — Word-locked files and legacy .doc/.dot.
    // A locked file is Not processed ("close it in Word"); a .doc/.dot is
    // converted through Word to a temp .docx and runs under its ORIGINAL
    // name. Every selected file lands in the run or in Not processed. ──
    setProgress({ phase: 'scan', done: -1, total: originalJobFiles.length });
    const notProcessedReason: Record<string, string> = {};
    let originalByRunPath: Record<string, string> = {};
    let runPaths: string[] = [];
    try {
      const pre = await preflightFormatterInputs(originalJobFiles, api || {});
      runPaths = pre.runPaths;
      originalByRunPath = pre.originalByRunPath;
      convertedRunPaths.push(...Object.keys(pre.originalByRunPath));
      Object.assign(notProcessedReason, pre.notProcessed);
    } catch (e: unknown) {
      abort();
      setRunWarning(
        `Couldn't check the files before the run (${e instanceof Error ? e.message : String(e)}).`
      );
      return;
    }
    /** The name the user picked — a converted .doc reports as itself. */
    const displayName = (runPath: string) => fileBaseName(originalByRunPath[runPath] || runPath);
    /** Not-processed rows are keyed by the file the user picked. */
    const pickedPath = (runPath: string) => originalByRunPath[runPath] || runPath;
    const notProcessedRows = () =>
      Object.entries(notProcessedReason).map(([file, reason]) => ({
        file: fileBaseName(file),
        success: false,
        notProcessedReason: reason,
      }));

    // ── 2. SCAN — read every file, no writes (formatting-artifacts R1) ──
    let scanEntries: ArtifactScanEntry[] = [];
    try {
      scanEntries =
        runPaths.length > 0
          ? await api.scanFormatterDocuments(safeConfig, runPaths, undefined, facets)
          : [];
    } catch (e: any) {
      abort();
      setRunWarning(`Couldn't scan the files (${e?.message || String(e)}).`);
      return;
    }
    setProgress(null);

    for (const e of scanEntries) if (e.error) notProcessedReason[pickedPath(e.file)] = e.error;
    let workingEntries = scanEntries.filter((e) => !e.error);
    const revisionActionByFile: Record<string, 'accept' | 'reject'> = {};

    // ── 3. TRACKED-CHANGES GATE ──────────────────────────────────────
    const trackedFiles = bodyOn ? workingEntries.filter((e) => e.hasTrackedChanges) : [];
    if (trackedFiles.length > 0) {
      const choice = await waitRevisionGate(trackedFiles);
      if (choice === 'cancel') {
        abort();
        return;
      }
      if (choice === 'skip') {
        for (const e of trackedFiles) {
          notProcessedReason[pickedPath(e.file)] =
            'Unaccepted tracked changes — skipped; resolve in Word first.';
        }
        workingEntries = workingEntries.filter((e) => !e.hasTrackedChanges);
      } else {
        for (const e of trackedFiles) revisionActionByFile[e.file] = choice;
        setProgress({ phase: 'scan', done: -1, total: trackedFiles.length });
        let rescanned: ArtifactScanEntry[] = [];
        try {
          rescanned = await api.scanFormatterDocuments(
            safeConfig,
            trackedFiles.map((e) => e.file),
            revisionActionByFile,
            facets
          );
        } catch (e: any) {
          abort();
          setRunWarning(
            `Couldn't re-scan after resolving tracked changes (${e?.message || String(e)}).`
          );
          return;
        }
        setProgress(null);
        const rescanMap = new Map(rescanned.map((r) => [r.file, r]));
        workingEntries = workingEntries
          .map((e) => {
            if (!revisionActionByFile[e.file]) return e;
            const r = rescanMap.get(e.file);
            if (!r) return e;
            if (r.error) {
              notProcessedReason[pickedPath(e.file)] = r.error;
              return null;
            }
            return r;
          })
          .filter((e): e is ArtifactScanEntry => !!e);
      }
    }

    // ── 4. ARTIFACTS PROMPT ──────────────────────────────────────────
    const filesWithArtifacts = workingEntries.filter((e) => (e.artifacts?.total || 0) > 0);
    const totalArtifacts = filesWithArtifacts.reduce((n, e) => n + (e.artifacts?.total || 0), 0);
    let reviewDecisionsByFile: Record<string, Record<string, DirtyDecision>> = {};
    const artifactsSkippedFiles = new Set<string>();

    if (totalArtifacts > 0) {
      const choice = await waitArtifactsPrompt(totalArtifacts, filesWithArtifacts.length);
      if (choice === 'cancel') {
        abort();
        return;
      }
      if (choice === 'review') {
        // ── 5. REVIEW — per-spec, typed verbs, auto-advance, no silent
        // shipping (handleReviewClose gates the promise this awaits). ──
        const filesForReview: DirtyFileResult[] = filesWithArtifacts.map((e) => ({
          file: e.file,
          revisionAction: revisionActionByFile[e.file],
          unheadered: e.artifacts,
        }));
        reviewDecisionsByFile = await reviewDirtyItems(filesForReview);
        setSavedDecisions(reviewDecisionsByFile);
      }
      // Whichever path (Skip, or Review with items left over the T7 confirm
      // let through), every file with anything still undecided is marked —
      // one brain, so "skip the whole batch" and "shipped 2 of 30 unresolved
      // after review" are the SAME accounting.
      for (const e of filesWithArtifacts) {
        const undecided = countUndecided(
          e.artifacts?.samples || [],
          reviewDecisionsByFile[e.file] || {}
        );
        if (undecided > 0) artifactsSkippedFiles.add(e.file);
      }
    } else {
      setSavedDecisions({});
    }

    // ── 6. FORMAT ─────────────────────────────────────────────────────
    let jobFiles = workingEntries.map((e) => e.file);
    let archivedFolder: string | null = null;
    let allResults: any[] = [];

    if (jobFiles.length > 0) {
      // A converted .doc runs from its temp .docx; the ORIGINAL is what gets
      // archived, and the run keeps reading the temp copy.
      const archived = await archiveForRun(jobFiles.map(pickedPath), overwriteMode, atRisk);
      if (!archived) {
        setRunning(false);
        return; // archiveForRun already restored the selection on failure
      }
      archivedFolder = archived.folder;
      setOriginalsMovedTo(archived.folder);
      jobFiles = jobFiles.map((f, i) => (originalByRunPath[f] ? f : archived.paths[i]));

      try {
        // One engine call per file so the counter is real progress, not a
        // guess. Concatenated per-file results are shape-identical to one
        // batch call.
        for (let i = 0; i < jobFiles.length; i++) {
          const e = workingEntries[i];
          setProgress({ phase: 'format', done: i, total: jobFiles.length });
          const decisions = reviewDecisionsByFile[e.file] || {};
          const engineDecisions = toEngineDecisions(e.artifacts?.samples || [], decisions);
          const directives: FormatFileDirectives = {
            revisionAction: revisionActionByFile[e.file],
            decisions: engineDecisions.length > 0 ? engineDecisions : undefined,
            artifactsSkippedByUser: artifactsSkippedFiles.has(e.file) || undefined,
          };
          const res = await api.formatDocuments(
            safeConfig,
            [jobFiles[i]],
            outputDir,
            facets,
            overwriteMode,
            { [jobFiles[i]]: directives }
          );
          if (Array.isArray(res))
            allResults.push(
              ...res.map((r: FormatResult) =>
                originalByRunPath[e.file] ? { ...r, file: displayName(e.file) } : r
              )
            );
        }
        // Every selected file shows in the results — Not processed ones too
        // (locked, legacy .doc that couldn't convert, scan errors).
        // D8: a body-off run's rows say "Body untouched" (formatResultNote).
        setResults([
          ...allResults.map((r) => (bodyOn ? r : { ...r, bodyUntouched: true })),
          ...notProcessedRows(),
        ]);

        // ── 7. REPORT — every selected file accounted for. Processed files
        // report against the PRE-format scan (`e.artifacts`) — the residual
        // scan (`allResults[i].unheadered`) is what the results-list badge
        // and its read-only reopen show instead, further down. ──
        const reportEntries = [
          ...workingEntries.map((e, i) => {
            const decisions = reviewDecisionsByFile[e.file] || {};
            const base = allResults[i] || {
              success: false,
              file: displayName(e.file),
            };
            return {
              ...base,
              bodyUntouched: !bodyOn || undefined,
              unheadered: e.artifacts,
              decisions,
              revisionDecision: revisionActionByFile[e.file],
              artifactsSkippedByUser: artifactsSkippedFiles.has(e.file)
                ? countUndecided(e.artifacts?.samples || [], decisions)
                : undefined,
            };
          }),
          ...notProcessedRows(),
        ];
        if (api?.writeFormatReport && reportEntries.length > 0) {
          try {
            const rep = await api.writeFormatReport({
              outputDir,
              formaName: safeConfig.docSettings?.standardName || 'Untitled',
              archiveFolder: archivedFolder,
              results: reportEntries,
            });
            if (rep?.reportPath) setReportPath(rep.reportPath);
          } catch (err: unknown) {
            // Report failure never blocks the run's results — but it is
            // never silent either (D3).
            reportWriteFailed(err);
          }
        }

        // Convert successful outputs to PDF if requested — same overwrite
        // choice applies ('rename' never clobbers an existing PDF).
        if (exportPdf && api?.convertToPdf) {
          const successFiles = allResults
            .filter((r: any) => r.success && r.outputPath)
            .map((r: any) => r.outputPath);
          if (successFiles.length > 0) {
            // ONE batch call — the converter opens a single Word instance
            // for the whole set. Per-file calls here boot Word once per
            // document and turn a ~10s batch into 30-60s (v2.14.13 fix).
            setProgress({ phase: 'pdf', done: -1, total: successFiles.length });
            const pdfRes = await api.convertToPdf(successFiles, outputDir, overwriteMode);
            if (Array.isArray(pdfRes)) setPdfResults(pdfRes);
          }
        }

        if (openAfterFormat && allResults.some((r: any) => r.success) && outputDir) {
          api.openFolder(outputDir);
        }
      } catch (err: any) {
        setResults([{ file: 'Error', success: false, error: err.message }]);
      }
    } else {
      // Nothing survived the gate/scan (every file was skipped or errored) —
      // still write the report so the "Not processed" section explains why.
      setResults(notProcessedRows());
      if (api?.writeFormatReport && Object.keys(notProcessedReason).length > 0) {
        try {
          const rep = await api.writeFormatReport({
            outputDir,
            formaName: safeConfig.docSettings?.standardName || 'Untitled',
            archiveFolder: null,
            results: notProcessedRows(),
          });
          if (rep?.reportPath) setReportPath(rep.reportPath);
        } catch (err: unknown) {
          reportWriteFailed(err);
        }
      }
    }

    setProgress(null);
    setRunning(false);
  }

  // Cockpit run: branch on which functions are on. Format runs the forma pipeline
  // (and converts to PDF too if that function is on); PDF-only converts the raw .docx.
  async function handleRun() {
    setRunWarning(null);
    if (!files.length) return setRunWarning('Add at least one Word file first.');
    if (!outputDir) return setRunWarning('Choose an output folder first.');
    if (!fnFormat && !exportPdf)
      return setRunWarning('Turn on at least one function — Format or Convert to PDF.');
    if (fnFormat) {
      await handleFormat();
    } else {
      await handlePdfOnly();
    }
  }

  // PDF-only runs keep the UNCONDITIONAL confirmation (a conversion batch has
  // no other summary of what it is about to do) and still fail closed: an
  // error checking the folder aborts before anything is processed.
  async function handlePdfOnly() {
    if (!(window as any).api?.convertToPdf)
      return setRunWarning('PDF conversion requires Microsoft Word.');
    let conflicts: string[] = [];
    if ((window as any).api?.checkPdfConflicts) {
      try {
        const res = await (window as any).api.checkPdfConflicts(files, outputDir);
        conflicts = res?.conflicts || [];
      } catch (e: any) {
        setRunWarning(
          `Couldn't check the output folder for existing PDF files — nothing was processed. (${e?.message || String(e)})`
        );
        return;
      }
    }
    setConfirmModal({
      fileCount: files.length,
      filePaths: [...files],
      formaName: null,
      outputDir,
      conflicts: conflicts.map((outputPath) => ({
        file: outputPath.split(/[/\\]/).pop() || outputPath,
        outputPath,
      })),
      onConfirm: (mode: OverwriteMode) => {
        setConfirmModal(null);
        doPdfOnly(mode);
      },
      onCancel: () => setConfirmModal(null),
    });
  }

  async function doPdfOnly(overwriteModeIn: OverwriteMode) {
    const overwriteMode = resolveOverwriteMode(overwriteModeIn);
    setRunning(true);
    setRunWarning(null);
    setResults(null);
    setPdfResults(null);
    setOriginalsMovedTo(null);
    // Archiving is a Format-run concept only (Tweaks 33): a PDF run never
    // touches the Word originals, so nothing is archived here.
    const jobFiles = [...files];
    setFiles([]);
    try {
      // ONE batch call — single Word instance for the whole set (per-file calls
      // boot Word per document; that was the v2.14.13 slowdown).
      setProgress({ phase: 'pdf', done: -1, total: jobFiles.length });
      const pdfRes = await (window as any).api.convertToPdf(jobFiles, outputDir, overwriteMode);
      setPdfResults(Array.isArray(pdfRes) ? pdfRes : []);
      if (openAfterFormat && outputDir && (window as any).api?.openFolder) {
        (window as any).api.openFolder(outputDir);
      }
    } catch (err: any) {
      setPdfResults([{ success: false, inputFile: 'Error', error: err.message }]);
    }
    setProgress(null);
    setRunning(false);
  }

  const CB = ({ k, label, desc }: { k: string; label: string; desc: string }) => {
    const on = facets[k];
    return (
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-2)',
          cursor: 'pointer',
          padding: `${rem(6)} 0`,
        }}
      >
        <div
          onClick={() => togFacet(k)}
          role="checkbox"
          aria-checked={on}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              togFacet(k);
            }
          }}
          style={{
            width: rem(18),
            height: rem(18),
            borderRadius: 'var(--radius-sm)',
            border: `2px solid ${on ? 'var(--ac)' : 'var(--bd)'}`,
            background: on ? 'var(--ac)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-inverse)',
            cursor: 'pointer',
            transition: 'all .15s',
            flexShrink: 0,
            marginTop: rem(1),
          }}
        >
          {on ? <Icon icon={Check} em={0.9231} aria-hidden="true" /> : null}
        </div>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--tx)',
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', marginTop: rem(1) }}>
            {desc}
          </div>
        </div>
      </label>
    );
  };

  return (
    <div style={{ padding: 'var(--space-5)', maxWidth: rem(1120), margin: '0 auto' }}>
      <h2
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          margin: '0 0 var(--space-1)',
        }}
      >
        Formatter
      </h2>
      <p
        style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)', margin: `0 0 ${rem(18)}` }}
      >
        One engine for every output — format raw files or convert to PDF.
      </p>
      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {/* Step 1 — files */}
        <div style={{ padding: `${rem(18)} ${rem(20)}` }}>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--tx)',
              marginBottom: rem(10),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            Files
            {files.length > 0 && (
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--ac)',
                  background: 'rgba(var(--acr),0.12)',
                  borderRadius: 'var(--radius-full)',
                  padding: `${rem(2)} var(--space-2)`,
                }}
              >
                {files.length} file{files.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            onClick={pickFiles}
            disabled={running}
            onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              if (!dragActive) setDragActive(true);
            }}
            onDragEnter={() => setDragActive(true)}
            onDragLeave={(e: React.DragEvent<HTMLButtonElement>) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragActive(false);
            }}
            onDrop={async (e: React.DragEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
              if (running) return;
              const getPath = window.api?.getPathForFile;
              const all = [...e.dataTransfer.files];
              // SPEC_WORKFLOW §12: every Word type is accepted (.doc .docx
              // .dot .dotx .dotm). A .doc/.dot that can't be converted shows
              // up as a Not processed row in the run, never a silent drop.
              const wordFiles = all.filter((f) => isWordFile(f.name));
              const skipped = all.length - wordFiles.length;
              if (skipped > 0)
                showDropNotice(
                  `Only Word files are supported — ${skipped} file${skipped > 1 ? 's' : ''} skipped.`
                );
              // getPathForFile resolves only after main has granted the file,
              // so the run's lock probe and scan never race the grant.
              const dropped = (
                await Promise.all(
                  wordFiles.map((f) =>
                    getPath ? getPath(f) : Promise.resolve((f as File & { path?: string }).path)
                  )
                )
              ).filter((p: unknown): p is string => typeof p === 'string' && p.length > 0);
              if (dropped.length) addFiles(dropped);
            }}
            style={{
              width: '100%',
              border: `2px dashed ${dragActive ? 'var(--ac)' : 'var(--bd)'}`,
              borderRadius: 'var(--radius-lg)',
              padding: rem(20),
              textAlign: 'center',
              cursor: running ? 'default' : 'pointer',
              opacity: running ? 0.6 : 1,
              background: dragActive ? 'rgba(var(--acr),0.06)' : 'transparent',
              color: 'var(--tx)',
              transition: 'border-color .15s, background .15s',
            }}
          >
            <div style={{ marginBottom: 'var(--space-2)', color: 'var(--tm)', opacity: 0.5 }}>
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'inline-block' }}
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M12 18v-6" />
                <path d="m9 15 3 3 3-3" />
              </svg>
            </div>
            <div
              style={{
                fontWeight: 'var(--font-weight-semibold)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              {files.length
                ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
                : 'Click to browse or drag Word files here'}
            </div>
          </button>
          {dropNotice && (
            <div
              role="status"
              aria-live="polite"
              style={{
                marginTop: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                fontSize: 'var(--font-size-base)',
                color: 'var(--color-warning)',
              }}
            >
              {dropNotice}
            </div>
          )}
          {files.length > 0 && (
            <div style={{ marginTop: rem(10), maxHeight: rem(200), overflow: 'auto' }}>
              {files.map((f, i) => (
                <div
                  key={i}
                  style={{
                    padding: `${rem(3)} 0`,
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tm)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--space-1)',
                  }}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0, opacity: 0.55, marginTop: rem(3) }}
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                  </svg>
                  <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                    <span style={{ color: 'var(--tx)', fontWeight: 'var(--font-weight-medium)' }}>
                      {f.split(/[/\\]/).pop()}
                    </span>
                    <span
                      style={{ display: 'block', fontSize: 'var(--font-size-sm)', opacity: 0.8 }}
                    >
                      {f}
                    </span>
                  </span>
                  <button
                    type="button"
                    disabled={running}
                    onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
                    aria-label={`Remove ${f.split(/[/\\]/).pop()}`}
                    title="Remove file"
                    style={{
                      marginLeft: 'auto',
                      cursor: running ? 'default' : 'pointer',
                      opacity: 0.5,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: rem(10),
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--tm)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <Icon icon={X} em={0.9231} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Step 2 — forma + parts, and/or convert to PDF */}
        <div style={{ padding: `${rem(18)} ${rem(20)}`, borderTop: '1px solid var(--bd)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--tx)',
              marginBottom: rem(10),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            Format &amp; convert
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {/* Function 1 — Format with a forma (expands to its forma + part chips) */}
            <div
              style={{
                borderRadius: rem(8),
                border: `1.5px solid ${fnFormat ? 'var(--ac)' : 'var(--bd)'}`,
                background: fnFormat ? 'rgba(var(--acr),0.05)' : 'transparent',
                overflow: 'visible',
                transition: 'all .15s',
              }}
            >
              <div
                onClick={() => setFnFormat((p) => !p)}
                role="checkbox"
                aria-checked={fnFormat}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFnFormat((p) => !p);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(10),
                  padding: `${rem(11)} var(--space-3)`,
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: rem(18),
                    height: rem(18),
                    borderRadius: 'var(--radius-sm)',
                    flexShrink: 0,
                    border: `2px solid ${fnFormat ? 'var(--ac)' : 'var(--bd)'}`,
                    background: fnFormat ? 'var(--ac)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-inverse)',
                  }}
                >
                  {fnFormat ? <Icon icon={Check} em={0.9231} aria-hidden="true" /> : null}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--tx)',
                    }}
                  >
                    Format with a forma
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      marginTop: rem(1),
                    }}
                  >
                    Apply a forma + the parts you choose
                  </div>
                </div>
              </div>
              {fnFormat && (
                <div
                  style={{
                    padding: `${rem(11)} var(--space-3) var(--space-3)`,
                    borderTop: '1px solid var(--bd)',
                  }}
                >
                  <SearchableSelectKV
                    value={sel}
                    options={standards
                      .filter((s) => !isArchived(s) || s.id === sel)
                      .map((s) => {
                        const hint = [s.docSettings?.clientName, s.docSettings?.bodyFont]
                          .filter(Boolean)
                          .join(' · ');
                        return {
                          value: s.id,
                          label: s.docSettings?.standardName || 'Untitled',
                          hint: hint || undefined,
                        };
                      })}
                    onChange={(v) => setSel(v)}
                    disabled={running}
                    placeholder="— Select forma —"
                    searchPlaceholder="Search formas…"
                  />
                  <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: rem(6), marginTop: rem(10) }}
                  >
                    {[
                      { k: 'body', label: 'Body' },
                      { k: 'headers', label: 'Headers' },
                      { k: 'footers', label: 'Footers' },
                      { k: 'prePost', label: 'Pre/Post' },
                      { k: 'pageSetup', label: 'Page setup' },
                    ].map((p) => (
                      <span
                        key={p.k}
                        onClick={() => togFacet(p.k)}
                        role="checkbox"
                        aria-checked={facets[p.k]}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            togFacet(p.k);
                          }
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 'var(--space-6)',
                          fontSize: 'var(--font-size-sm)',
                          padding: `0 ${rem(14)}`,
                          borderRadius: rem(20),
                          cursor: 'pointer',
                          userSelect: 'none',
                          border: `1px solid ${facets[p.k] ? 'var(--ac)' : 'var(--bd)'}`,
                          background: facets[p.k] ? 'rgba(var(--acr),0.12)' : 'transparent',
                          color: facets[p.k] ? 'var(--ac)' : 'var(--tm)',
                          transition: 'all .12s',
                        }}
                      >
                        {p.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Function 2 — Convert to PDF (disabled when Word isn't detected) */}
            <div
              onClick={() => {
                if (!pdfBlocked) setExportPdf((p) => !p);
              }}
              role="checkbox"
              aria-checked={exportPdf}
              aria-disabled={pdfBlocked}
              tabIndex={pdfBlocked ? -1 : 0}
              onKeyDown={(e) => {
                if (pdfBlocked) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExportPdf((p) => !p);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: rem(10),
                padding: `${rem(11)} var(--space-3)`,
                borderRadius: rem(8),
                border: `1.5px solid ${exportPdf ? 'var(--ac)' : 'var(--bd)'}`,
                background: exportPdf ? 'rgba(var(--acr),0.05)' : 'transparent',
                cursor: pdfBlocked ? 'default' : 'pointer',
                opacity: pdfBlocked ? 0.6 : 1,
                transition: 'all .15s',
              }}
            >
              <div
                style={{
                  width: rem(18),
                  height: rem(18),
                  borderRadius: 'var(--radius-sm)',
                  flexShrink: 0,
                  marginTop: rem(1),
                  border: `2px solid ${exportPdf ? 'var(--ac)' : 'var(--bd)'}`,
                  background: exportPdf ? 'var(--ac)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-text-inverse)',
                }}
              >
                {exportPdf ? <Icon icon={Check} em={0.9231} aria-hidden="true" /> : null}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--tx)',
                  }}
                >
                  Convert to PDF
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--tm)',
                      marginLeft: rem(6),
                      fontWeight: 'var(--font-weight-regular)',
                    }}
                  >
                    (needs Word)
                  </span>
                </div>
                {pdfBlocked ? (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-warning)',
                      marginTop: rem(1),
                    }}
                  >
                    Requires Microsoft Word on this machine — not detected
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      marginTop: rem(1),
                    }}
                  >
                    {fnFormat ? 'Convert the formatted output' : 'Convert the input .docx as-is'}
                  </div>
                )}
              </div>
            </div>

            {fnFormat && !anyFacet && (
              <div
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-warning)',
                }}
              >
                Turn on at least one part to format (Body, Headers, …) above.
              </div>
            )}
          </div>
        </div>

        {/* Step 3 — destination: output folder + optional archive */}
        <div style={{ padding: `${rem(18)} ${rem(20)}`, borderTop: '1px solid var(--bd)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--tx)',
              marginBottom: rem(10),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            Output &amp; archive
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 'var(--space-4)',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--tm)',
                  marginBottom: rem(6),
                }}
              >
                Output folder
              </div>
              <button
                onClick={pickOutputDir}
                disabled={running}
                title={outputDir || undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: `${rem(10)} var(--space-3)`,
                  borderRadius: rem(8),
                  border: '1px solid var(--bd)',
                  background: 'var(--sa)',
                  color: outputDir ? 'var(--tx)' : 'var(--tm)',
                  fontSize: 'var(--font-size-base)',
                  cursor: running ? 'default' : 'pointer',
                  opacity: running ? 0.6 : 1,
                  fontWeight: 'var(--font-weight-medium)',
                  textAlign: 'left',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, opacity: 0.7 }}
                >
                  <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
                </svg>
                <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                  {outputDir || 'Choose output folder'}
                </span>
              </button>
            </div>
            <div>
              <div
                style={{
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--tm)',
                  marginBottom: rem(6),
                }}
              >
                Archive originals{' '}
                <span style={{ fontWeight: 'var(--font-weight-medium)' }}>(optional)</span>
              </div>
              <div style={{ display: 'flex', gap: rem(6), alignItems: 'center' }}>
                <button
                  onClick={pickArchiveDir}
                  disabled={running}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: `${rem(10)} var(--space-3)`,
                    borderRadius: rem(8),
                    border: '1px solid var(--bd)',
                    background: 'var(--sa)',
                    color: archiveDir ? 'var(--tx)' : 'var(--tm)',
                    fontSize: 'var(--font-size-base)',
                    cursor: running ? 'default' : 'pointer',
                    opacity: running ? 0.6 : 1,
                    fontWeight: 'var(--font-weight-medium)',
                    textAlign: 'left',
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0, opacity: 0.7 }}
                  >
                    <rect x="2" y="3" width="20" height="5" rx="1" />
                    <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
                    <path d="M10 12h4" />
                  </svg>
                  <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                    {archiveDir || 'Choose archive folder'}
                  </span>
                </button>
                {archiveDir && (
                  <button
                    type="button"
                    disabled={running}
                    onClick={() => setArchiveDirAndSave('')}
                    aria-label="Turn off archiving"
                    title="Turn off archiving"
                    style={{
                      cursor: running ? 'default' : 'pointer',
                      opacity: 0.6,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: rem(10),
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--tm)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <Icon icon={X} size="md" aria-hidden="true" />
                  </button>
                )}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--tm)',
                  marginTop: 'var(--space-1)',
                }}
              >
                {fnFormat
                  ? 'Inputs move to a dated folder before the run.'
                  : 'Applies to Format runs only.'}
              </div>
            </div>
          </div>
        </div>

        {/* Step 4 — run (the primary CTA is self-explanatory; no redundant
            "Run" label above it — avoids a second "Run"-named element next
            to the button for screen readers and automation alike) */}
        <div style={{ padding: `${rem(18)} ${rem(20)}`, borderTop: '1px solid var(--bd)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {/* Why Run is disabled — listed inline, same treatment as the parts warning */}
            {!running && runBlockers.length > 0 && (
              <div
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-warning)',
                }}
              >
                {runBlockers.map((b, i) => (
                  <div key={i} style={{ padding: `${rem(1)} 0` }}>
                    {b}
                  </div>
                ))}
              </div>
            )}

            {/* Inline run warning — replaces the old native alert()s */}
            {runWarning && (
              <div
                role="alert"
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-warning)',
                }}
              >
                {runWarning}
              </div>
            )}

            <Button
              onClick={handleRun}
              disabled={runDisabled}
              title={!canRun ? "You don't have permission to format documents" : undefined}
              className="w-full"
              size="lg"
              style={{
                padding: `${rem(14)} var(--space-3)`,
                height: 'auto',
                borderRadius: rem(9),
                fontWeight: 'var(--font-weight-bold)',
                fontSize: 'var(--font-size-lg)',
                boxShadow: runDisabled ? 'none' : '0 6px 16px rgba(var(--acr),0.3)',
              }}
            >
              {running ? (
                <>
                  <Icon icon={Loader2} size="md" aria-hidden="true" className="animate-spin" />
                  <span aria-live="polite">
                    {progress
                      ? progress.done < 0
                        ? progress.phase === 'scan'
                          ? `Scanning ${progress.total} file${progress.total === 1 ? '' : 's'}…`
                          : `Converting ${progress.total} file${progress.total === 1 ? '' : 's'} to PDF…`
                        : `${progress.phase === 'format' ? 'Formatting' : 'Converting to PDF'} ${Math.min(
                            progress.done + 1,
                            progress.total
                          )} of ${progress.total}…`
                      : 'Running…'}
                  </span>
                </>
              ) : (
                <>
                  <Icon icon={Play} size="md" aria-hidden="true" />
                  {`Run${files.length ? ` · ${files.length} file${files.length > 1 ? 's' : ''}` : ''}`}
                </>
              )}
            </Button>
            <label
              onClick={() => setOpenAfterFormatAndSave(!openAfterFormat)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: rem(6),
                cursor: 'pointer',
                minHeight: 'var(--space-6)',
                fontSize: 'var(--font-size-base)',
                color: 'var(--tm)',
              }}
            >
              <div
                role="checkbox"
                aria-checked={openAfterFormat}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpenAfterFormatAndSave(!openAfterFormat);
                  }
                }}
                style={{
                  width: rem(14),
                  height: rem(14),
                  borderRadius: rem(3),
                  border: `1.5px solid ${openAfterFormat ? 'var(--ac)' : 'var(--bd)'}`,
                  background: openAfterFormat ? 'var(--ac)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-text-inverse)',
                  flexShrink: 0,
                  transition: 'all .15s',
                }}
              >
                {openAfterFormat ? <Icon icon={Check} em={0.6923} aria-hidden="true" /> : null}
              </div>
              Open output folder when done
            </label>
          </div>
        </div>

        {/* Results — integrated into the same flow, appears once a run finishes */}
        {(results || pdfResults || originalsMovedTo) && (
          <div style={{ padding: `${rem(18)} ${rem(20)}`, borderTop: '1px solid var(--bd)' }}>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--tx)',
                marginBottom: rem(10),
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
              }}
            >
              Results
            </div>
            {originalsMovedTo && (
              <div
                style={{
                  padding: `${rem(6)} var(--space-2)`,
                  marginBottom: rem(6),
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--sa)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--tm)',
                  overflowWrap: 'anywhere',
                }}
              >
                <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--tx)' }}>
                  Originals moved to:{' '}
                </span>
                {originalsMovedTo.split('; ').map((f, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => (window as any).api?.openFolder?.(f)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      marginRight: 'var(--space-2)',
                      color: 'var(--ac)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      font: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}
            {results && (
              <div>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--tm)',
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  Formatting results
                </div>
                {reportPath && (
                  <div
                    style={{
                      padding: `${rem(6)} var(--space-2)`,
                      marginBottom: rem(6),
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--sa)',
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      color: 'var(--tm)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--tx)' }}>
                      Report:{' '}
                    </span>
                    {reportPath}
                  </div>
                )}
                {results.map((r, i) => (
                  <div
                    key={i}
                    style={{ padding: `${rem(6)} 0`, borderBottom: '1px solid var(--bd)' }}
                  >
                    <div
                      style={{
                        fontSize: 'var(--font-size-base)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: rem(6),
                      }}
                    >
                      {r.success ? (
                        <Icon
                          icon={CheckCircle2}
                          em={1.2308}
                          aria-hidden="true"
                          style={{ color: 'var(--ok)', flexShrink: 0, marginTop: rem(2) }}
                        />
                      ) : (
                        <Icon
                          icon={XCircle}
                          em={1.2308}
                          aria-hidden="true"
                          style={{ color: 'var(--dg)', flexShrink: 0, marginTop: rem(2) }}
                        />
                      )}
                      <span className="sr-only">
                        {r.success
                          ? 'Succeeded: '
                          : r.notProcessedReason
                            ? 'Not processed: '
                            : 'Failed: '}
                      </span>
                      <span
                        style={{
                          fontWeight: 'var(--font-weight-semibold)',
                          flex: 1,
                          minWidth: 0,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {r.file}
                      </span>
                      {r.success ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted"
                          title="Show in folder"
                          aria-label={`Show ${r.file} in folder`}
                          onClick={() => {
                            const p = (r as any).outputPath;
                            const dir = p ? p.replace(/[\\/][^\\/]*$/, '') : outputDir;
                            if (dir) (window as any).api?.openFolder?.(dir);
                          }}
                        >
                          <Icon icon={FolderOpen} size="md" strokeWidth={1.8} />
                        </Button>
                      ) : (
                        <span
                          title={r.error}
                          style={{
                            color: 'var(--dg)',
                            fontSize: 'var(--font-size-sm)',
                            maxWidth: '55%',
                            overflowWrap: 'anywhere',
                            textAlign: 'right',
                            marginTop: rem(2),
                          }}
                        >
                          {r.notProcessedReason || friendlyError(r.error, 'Formatting')}
                        </span>
                      )}
                    </div>
                    {formatResultNote(r) && (
                      <div
                        style={{
                          marginTop: 'var(--space-1)',
                          marginLeft: rem(22),
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {formatResultNote(r)}
                      </div>
                    )}
                    {r.success &&
                      ((r.unheadered?.total || 0) > 0 ||
                        (r.unheadered?.fixed?.length || 0) > 0) && (
                        <div style={{ marginTop: 'var(--space-1)', marginLeft: rem(22) }}>
                          <Hint
                            text={[
                              (r.unheadered!.fixed?.length || 0) > 0
                                ? `${r.unheadered!.fixed!.length} auto-fixed`
                                : '',
                              r.unheadered!.total > 0
                                ? `${r.unheadered!.total} remaining${
                                    savedDecisions[r.file] ? ' — reviewed' : ''
                                  }`
                                : '',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          >
                            <button
                              type="button"
                              onClick={() => openReadOnlyReview(r)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: rem(5),
                                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                                fontWeight: 'var(--font-weight-semibold)',
                                color:
                                  r.unheadered!.total > 0 ? 'var(--color-warning)' : 'var(--ok)',
                                background: 'transparent',
                                border: 'none',
                                padding: `${rem(9)} ${rem(2)}`,
                                cursor: 'pointer',
                              }}
                            >
                              <Icon
                                icon={AlertTriangle}
                                em={1.0833}
                                aria-hidden="true"
                                style={{ flexShrink: 0 }}
                              />
                              Artifacts
                            </button>
                          </Hint>
                          {(() => {
                            // Flag for editing (R2, 2026-09-08): the
                            // formatter never touches a spec's CONTENT, so a
                            // flagged item still needs a hand fix in Word —
                            // called out here so it isn't buried behind a
                            // tooltip hover.
                            const flaggedCount = Object.values(savedDecisions[r.file] || {}).filter(
                              (d) => d.action === 'flag'
                            ).length;
                            return flaggedCount > 0 ? (
                              <div
                                style={{
                                  marginTop: 'var(--space-1)',
                                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                                  fontWeight: 'var(--font-weight-semibold)',
                                  color: 'var(--color-caution, var(--color-warning))',
                                }}
                              >
                                {flaggedCount} flagged for editing — fix in Word
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
            {pdfResults && (
              <div style={{ marginTop: results ? 8 : 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--tm)',
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  PDF export results
                </div>
                {pdfResults.map((r: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      padding: `${rem(6)} 0`,
                      fontSize: 'var(--font-size-base)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: rem(6),
                      borderBottom: '1px solid var(--bd)',
                    }}
                  >
                    {r.success ? (
                      <Icon
                        icon={CheckCircle2}
                        em={1.2308}
                        aria-hidden="true"
                        style={{ color: 'var(--ok)', flexShrink: 0, marginTop: rem(2) }}
                      />
                    ) : (
                      <Icon
                        icon={XCircle}
                        em={1.2308}
                        aria-hidden="true"
                        style={{ color: 'var(--dg)', flexShrink: 0, marginTop: rem(2) }}
                      />
                    )}
                    <span className="sr-only">
                      {r.success
                        ? 'Succeeded: '
                        : r.notProcessedReason
                          ? 'Not processed: '
                          : 'Failed: '}
                    </span>
                    <span
                      style={{
                        fontWeight: 'var(--font-weight-semibold)',
                        flex: 1,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {(r.outputFile || r.inputFile || '').split(/[/\\]/).pop()}
                    </span>
                    {r.success ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted"
                        title="Show in folder"
                        aria-label={`Show ${(r.outputFile || r.inputFile || '').split(/[/\\]/).pop()} in folder`}
                        onClick={() => {
                          const p = r.outputFile;
                          const dir = p ? p.replace(/[\\/][^\\/]*$/, '') : outputDir;
                          if (dir) (window as any).api?.openFolder?.(dir);
                        }}
                      >
                        <Icon icon={FolderOpen} size="md" strokeWidth={1.8} />
                      </Button>
                    ) : (
                      <span
                        title={r.error}
                        style={{
                          color: 'var(--dg)',
                          fontSize: 'var(--font-size-sm)',
                          maxWidth: '55%',
                          overflowWrap: 'anywhere',
                          textAlign: 'right',
                          marginTop: rem(2),
                        }}
                      >
                        {friendlyError(r.error, 'PDF conversion')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Always-on pre-run confirmation — every batch run stops here first,
          conflicts or not (2026-08-14 "never again" fix). Kit Dialog: focus
          trap, Esc-to-close, click-outside-to-close, portal. */}
      <Dialog
        open={!!confirmModal}
        onOpenChange={(o) => {
          if (!o) confirmModal?.onCancel();
        }}
      >
        {confirmModal && (
          <DialogContent className="max-w-2xl" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                {confirmModal.formaName ? 'Confirm batch format' : 'Confirm PDF conversion'}
              </DialogTitle>
            </DialogHeader>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                <div>
                  <strong>{confirmModal.fileCount}</strong> file
                  {confirmModal.fileCount === 1 ? '' : 's'} to process
                </div>
                {confirmModal.filePaths.length > 0 && (
                  <div
                    style={{
                      maxHeight: rem(140),
                      overflow: 'auto',
                      padding: 'var(--space-2)',
                      background: 'var(--ib)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    {confirmModal.filePaths.map((p, i) => (
                      <div
                        key={i}
                        style={{
                          padding: `${rem(2)} 0`,
                          color: 'var(--tm)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: rem(6),
                        }}
                      >
                        <Icon
                          icon={FileText}
                          size="md"
                          aria-hidden="true"
                          style={{ flexShrink: 0, opacity: 0.7, marginTop: rem(2) }}
                        />
                        <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{p}</span>
                      </div>
                    ))}
                  </div>
                )}
                {confirmModal.formaName && (
                  <div>
                    Forma: <strong>{confirmModal.formaName}</strong>
                  </div>
                )}
                <div style={{ overflowWrap: 'anywhere', color: 'var(--tm)' }}>
                  Output folder: {confirmModal.outputDir}
                </div>
                {confirmModal.archiveDir && (
                  <div style={{ overflowWrap: 'anywhere', color: 'var(--tm)' }}>
                    Originals move to a dated folder in: {confirmModal.archiveDir}
                  </div>
                )}
              </div>

              {confirmModal.checkFailed && (
                <div
                  role="alert"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                  }}
                >
                  <Icon
                    icon={AlertTriangle}
                    em={1.0769}
                    aria-hidden="true"
                    style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: rem(2) }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 'var(--font-weight-bold)' }}>
                      Couldn't verify the output folder
                    </div>
                    <div style={{ color: 'var(--tm)', overflowWrap: 'anywhere' }}>
                      {confirmModal.checkFailed} Nothing existing will be replaced — the run can
                      only save under a new name.
                    </div>
                  </div>
                </div>
              )}

              {confirmModal.checkFailed ? null : confirmModal.conflicts.length > 0 ? (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rem(6),
                      fontWeight: 'var(--font-weight-bold)',
                      marginBottom: rem(6),
                    }}
                  >
                    <Icon
                      icon={AlertTriangle}
                      em={1.0769}
                      aria-hidden="true"
                      style={{ color: 'var(--color-warning)', flexShrink: 0 }}
                    />
                    {confirmModal.conflicts.length} existing file
                    {confirmModal.conflicts.length === 1 ? '' : 's'} will be affected
                  </div>
                  <div
                    style={{
                      maxHeight: rem(120),
                      overflow: 'auto',
                      padding: 'var(--space-2)',
                      background: 'var(--ib)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    {confirmModal.conflicts.map((c, i) => (
                      <div
                        key={i}
                        style={{
                          padding: `${rem(2)} 0`,
                          color: 'var(--tm)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: rem(6),
                        }}
                      >
                        <Icon
                          icon={FileText}
                          size="md"
                          aria-hidden="true"
                          style={{ flexShrink: 0, opacity: 0.7, marginTop: rem(2) }}
                        />
                        <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                          {c.outputPath || c.file}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      marginTop: rem(6),
                    }}
                  >
                    "Keep both" saves the new file under a different name — nothing existing is
                    touched. "Overwrite" backs up each existing file first, then replaces it.
                  </div>
                  {confirmModal.archiveDir && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--tm)',
                        marginTop: 'var(--space-1)',
                      }}
                    >
                      {`Originals move to ${confirmModal.archiveDir}.`}
                    </div>
                  )}
                  {confirmModal.moveOriginals && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--tx)',
                        marginTop: 'var(--space-2)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      Overwrite will first move {confirmModal.moveOriginals.files.length} original
                      {confirmModal.moveOriginals.files.length === 1 ? '' : 's'} into{' '}
                      {confirmModal.moveOriginals.folders.join('; ')}
                      {'\\<date and time>'}:
                      <div style={{ maxHeight: rem(96), overflowY: 'auto', color: 'var(--tm)' }}>
                        {confirmModal.moveOriginals.files.map((f, i) => (
                          <div key={i}>{f}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: 'var(--tm)' }}>No existing files will be overwritten.</div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={confirmModal.onCancel}>
                Cancel
              </Button>
              {confirmModal.checkFailed ? (
                // Overwrite is withheld when the folder couldn't be checked —
                // the run may only take the non-destructive path.
                <Button size="sm" onClick={() => confirmModal.onConfirm('rename')}>
                  Keep both (rename)
                </Button>
              ) : confirmModal.conflicts.length > 0 ? (
                <>
                  <Button size="sm" onClick={() => confirmModal.onConfirm('rename')}>
                    Keep both (rename)
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => confirmModal.onConfirm('overwrite')}
                  >
                    {confirmModal.moveOriginals ? 'Overwrite and move originals' : 'Overwrite'}
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={() => confirmModal.onConfirm('rename')}>
                  {confirmModal.formaName ? 'Format' : 'Convert'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* T5 — tracked-changes gate. One all-or-nothing choice for every file
          in the batch that carries unaccepted Word revisions, asked BEFORE
          the artifacts scan is even shown. The app never resolves revisions
          silently. */}
      <Dialog open={!!revisionGate} onOpenChange={(o) => !o && revisionGate?.onCancel()}>
        {revisionGate && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Unaccepted tracked changes</DialogTitle>
              <DialogDescription>
                {revisionGate.entries.length} file{revisionGate.entries.length === 1 ? '' : 's'}{' '}
                still carry{revisionGate.entries.length === 1 ? 's' : ''} unaccepted Word revisions.
                SpecForma never accepts or rejects a tracked change on its own — pick what happens
                to every one of these before formatting continues.
              </DialogDescription>
            </DialogHeader>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              <div
                style={{
                  maxHeight: 'calc(var(--space-8) * 2.5)',
                  overflow: 'auto',
                  padding: 'var(--space-2)',
                  background: 'var(--ib)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                {revisionGate.entries.map((e, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 'calc(var(--space-1) / 2) 0',
                      color: 'var(--tm)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 'calc(var(--space-1) * 1.5)',
                    }}
                  >
                    <Icon
                      icon={FileText}
                      size="md"
                      aria-hidden="true"
                      style={{ flexShrink: 0, opacity: 0.7, marginTop: 'calc(var(--space-1) / 2)' }}
                    />
                    <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                      {e.file}
                      {e.trackedChanges?.author || e.trackedChanges?.date ? (
                        <span style={{ color: 'var(--tm)' }}>
                          {' — '}
                          {[e.trackedChanges.author, e.trackedChanges.date]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                "Skip these files" leaves them out of this run — fix them in Word first.
              </div>
            </div>
            <DialogFooter className="justify-between">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setRevisionRejectConfirm(true)}
              >
                Reject all
              </Button>
              <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
                <Button variant="outline" size="sm" onClick={revisionGate.onCancel}>
                  Cancel
                </Button>
                <Button variant="outline" size="sm" onClick={() => revisionGate.onChoice('skip')}>
                  Skip these files
                </Button>
                <Button size="sm" onClick={() => revisionGate.onChoice('accept')}>
                  Accept all
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Reject all is destructive (discards every other author's Word
          edits across the whole batch, no undo) — gated through the kit
          ConfirmDialog; revisionGate.onChoice('reject') only fires on
          confirm. */}
      <ConfirmDialog
        open={revisionRejectConfirm && !!revisionGate}
        onOpenChange={(o) => !o && setRevisionRejectConfirm(false)}
        title="Reject all tracked changes?"
        description={`Every insertion and deletion in ${revisionGate?.entries.length ?? 0} file${
          revisionGate?.entries.length === 1 ? '' : 's'
        } is discarded and the original text restored. This cannot be undone.`}
        confirmLabel="Reject all"
        destructive
        onConfirm={() => {
          setRevisionRejectConfirm(false);
          revisionGate?.onChoice('reject');
        }}
      />

      {/* T4 — artifacts prompt. The forcing function: review your own spec's
          artifacts, or make an explicit, on-the-record choice to skip. */}
      <Dialog open={!!artifactsPrompt} onOpenChange={(o) => !o && artifactsPrompt?.onCancel()}>
        {artifactsPrompt && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Found {artifactsPrompt.totalArtifacts} formatting artifact
                {artifactsPrompt.totalArtifacts === 1 ? '' : 's'} across {artifactsPrompt.fileCount}{' '}
                file{artifactsPrompt.fileCount === 1 ? '' : 's'}
              </DialogTitle>
              <DialogDescription>
                These are paragraphs and section endings the forma can't place with confidence —
                stray returns, manual page breaks, missing heading levels, nonstandard section ends,
                or leftover pre-body lines. Review lets you decide each one; Skip formats the batch
                as-is and records every artifact as skipped by you in the report.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={artifactsPrompt.onCancel}>
                Cancel
              </Button>
              <Button variant="secondary" size="sm" onClick={artifactsPrompt.onSkip}>
                Skip and format
              </Button>
              <Button size="sm" onClick={artifactsPrompt.onReview}>
                Review
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* T7 — no silent shipping. Done/Esc/backdrop on the review with
          undecided items left stops here first. */}
      <ConfirmDialog
        open={!!unresolvedConfirm}
        onOpenChange={(o) => {
          if (!o) {
            unresolvedConfirm?.resolve(false);
            setUnresolvedConfirm(null);
          }
        }}
        title="Ship with unresolved artifacts?"
        description={`${unresolvedConfirm?.count ?? 0} formatting artifact${
          unresolvedConfirm?.count === 1 ? '' : 's'
        } ${unresolvedConfirm?.count === 1 ? 'is' : 'are'} still undecided. They'll format as-is, and the report will list them as Kept (no decision).`}
        confirmLabel="Ship anyway"
        cancelLabel="Keep reviewing"
        destructive={false}
        onConfirm={() => {
          unresolvedConfirm?.resolve(true);
          setUnresolvedConfirm(null);
        }}
      />

      {/* Pre-format review (formatting-artifacts R1): LIVE — reads the
          ORIGINAL input files, blocks formatting until every artifact is
          decided or the T7 confirm explicitly lets the run ship with some
          left unresolved. */}
      <DirtyReviewDialog
        open={!!liveReview}
        readOnly={false}
        files={liveReview?.files || []}
        decisions={liveDecisions}
        onDecide={handleLiveDecide}
        onKeepAllRemaining={handleKeepAllRemaining}
        onClose={handleReviewClose}
      />

      {/* Read-only reopen from a file's "Artifacts" badge, after the run —
          renders the saved OUTPUT and its residual scan. */}
      <DirtyReviewDialog
        open={!!viewReview}
        readOnly
        files={viewReview?.files || []}
        decisions={
          viewReview
            ? { [viewReview.files[0].file]: savedDecisions[viewReview.files[0].file] || {} }
            : {}
        }
        onDecide={() => {}}
        onKeepAllRemaining={() => {}}
        onClose={() => setViewReview(null)}
      />
    </div>
  );
}

/* ─── Shared modal styles (PDF converter prompts) ─── */
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  border: '1px solid var(--bd)',
  boxShadow: '0 8px 32px rgba(0,0,0,.2)',
  maxHeight: '80vh',
  overflow: 'auto',
};
const secondaryBtn: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  cursor: 'pointer',
};

/* ─── Standalone PDF Converter ─── */
function PdfConverter() {
  const [pdfFiles, setPdfFiles] = eUseState<string[]>([]);
  const [pdfOutputDir, setPdfOutputDir] = eUseState('');
  const [pdfRunning, setPdfRunning] = eUseState(false);
  const [pdfRes, setPdfRes] = eUseState<any[] | null>(null);
  const [pdfOpenAfter, setPdfOpenAfter] = eUseState(true);
  // Protective prompts before any overwrite or silent folder creation.
  const [pdfModal, setPdfModal] = eUseState<{
    kind: 'folder' | 'conflict';
    conflicts?: string[];
  } | null>(null);

  async function pickPdfFiles() {
    if (!(window as any).api) return;
    const paths = await (window as any).api.openDocxDialog();
    if (paths && paths.length) setPdfFiles(paths);
  }
  async function pickPdfOutputDir() {
    if (!(window as any).api) return;
    const dir = await (window as any).api.selectFolderDialog();
    if (dir) setPdfOutputDir(dir);
  }
  // Pre-flight: never overwrite or silently create a folder without asking.
  async function handleConvert() {
    if (!pdfFiles.length) return;
    if ((window as any).api?.checkPdfConflicts) {
      try {
        const { folderMissing, conflicts } = await (window as any).api.checkPdfConflicts(
          pdfFiles,
          pdfOutputDir || undefined
        );
        if (folderMissing) {
          setPdfModal({ kind: 'folder' });
          return;
        }
        if (conflicts && conflicts.length > 0) {
          setPdfModal({ kind: 'conflict', conflicts });
          return;
        }
      } catch (e) {
        console.warn('checkPdfConflicts failed, proceeding:', e);
      }
    }
    doConvert('overwrite');
  }

  async function doConvert(overwriteMode: 'overwrite' | 'rename') {
    setPdfModal(null);
    setPdfRunning(true);
    setPdfRes(null);
    try {
      const res = await (window as any).api.convertToPdf(
        pdfFiles,
        pdfOutputDir || undefined,
        overwriteMode
      );
      setPdfRes(res);
      if (pdfOpenAfter && res?.some((r: any) => r.success)) {
        const outDir =
          pdfOutputDir || (pdfFiles[0] ? pdfFiles[0].replace(/[/\\][^/\\]+$/, '') : '');
        if (outDir) (window as any).api.openFolder(outDir);
      }
    } catch (err: any) {
      setPdfRes([{ success: false, inputFile: 'Error', error: err.message }]);
    }
    setPdfRunning(false);
  }

  return (
    <>
      {pdfModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPdfModal(null);
          }}
          style={overlayStyle}
        >
          <div style={{ ...modalStyle, width: rem(460) }} onMouseDown={(e) => e.stopPropagation()}>
            {pdfModal.kind === 'folder' ? (
              <>
                <div
                  style={{
                    fontWeight: 'var(--font-weight-bold)',
                    fontSize: 'calc(var(--font-size-base) * 15 / 13)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Output folder not found
                </div>
                <div
                  style={{
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    color: 'var(--tm)',
                    marginBottom: rem(6),
                    lineHeight: 1.5,
                  }}
                >
                  This folder doesn’t exist:
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: "'JetBrains Mono', monospace",
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    borderRadius: 'var(--radius-md)',
                    padding: `${rem(7)} ${rem(10)}`,
                    marginBottom: 'var(--space-4)',
                    wordBreak: 'break-all',
                  }}
                >
                  {pdfOutputDir}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                  <button onClick={() => setPdfModal(null)} style={secondaryBtn}>
                    Cancel
                  </button>
                  <button
                    onClick={() => doConvert('overwrite')}
                    style={{
                      padding: `var(--space-2) ${rem(18)}`,
                      borderRadius: 'var(--radius-md)',
                      border: 'none',
                      background: 'var(--ac)',
                      color: '#fff',
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      fontWeight: 'var(--font-weight-semibold)',
                      cursor: 'pointer',
                    }}
                  >
                    Create folder &amp; convert
                  </button>
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    fontWeight: 'var(--font-weight-bold)',
                    fontSize: 'calc(var(--font-size-base) * 15 / 13)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  {pdfModal.conflicts!.length} PDF
                  {pdfModal.conflicts!.length === 1 ? '' : 's'} already exist
                  {pdfModal.conflicts!.length === 1 ? 's' : ''}
                </div>
                <div
                  style={{
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    color: 'var(--tm)',
                    marginBottom: 'var(--space-2)',
                    lineHeight: 1.5,
                  }}
                >
                  These output files are already in the folder:
                </div>
                <div
                  style={{
                    maxHeight: rem(150),
                    overflow: 'auto',
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    borderRadius: 'var(--radius-md)',
                    padding: `${rem(6)} var(--space-2)`,
                    marginBottom: 'var(--space-4)',
                  }}
                >
                  {pdfModal.conflicts!.map((f, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        fontFamily: "'JetBrains Mono', monospace",
                        color: 'var(--tm)',
                        padding: `${rem(2)} 0`,
                      }}
                    >
                      {f.replace(/^.*[/\\]/, '')}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                  <button onClick={() => setPdfModal(null)} style={secondaryBtn}>
                    Cancel
                  </button>
                  <button onClick={() => doConvert('rename')} style={secondaryBtn}>
                    Keep both (rename)
                  </button>
                  <button
                    onClick={() => doConvert('overwrite')}
                    style={{
                      padding: `var(--space-2) ${rem(18)}`,
                      borderRadius: 'var(--radius-md)',
                      border: 'none',
                      background: 'var(--dg, #c0392b)',
                      color: '#fff',
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      fontWeight: 'var(--font-weight-semibold)',
                      cursor: 'pointer',
                    }}
                  >
                    Overwrite
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div
        style={{
          padding: 'var(--space-3) var(--space-4)',
          borderBottom: '1px solid var(--bd)',
          fontWeight: 'var(--font-weight-semibold)',
          fontSize: 'var(--font-size-md)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        Convert to PDF
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-regular)',
            color: 'var(--tm)',
          }}
        >
          Convert Word files to PDF without formatting
        </span>
      </div>
      <div
        style={{
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <button
          onClick={pickPdfFiles}
          onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={async (e: React.DragEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const getPath = window.api?.getPathForFile;
            const picked = [...e.dataTransfer.files].filter((f) => isWordFile(f.name));
            const dropped = (
              await Promise.all(
                picked.map((f) =>
                  getPath ? getPath(f) : Promise.resolve((f as File & { path?: string }).path)
                )
              )
            ).filter((p: unknown): p is string => typeof p === 'string' && p.length > 0);
            if (dropped.length) setPdfFiles((p) => [...p, ...dropped]);
          }}
          style={{
            width: '100%',
            border: '2px dashed var(--bd)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            textAlign: 'center',
            cursor: 'pointer',
            background: 'transparent',
            color: 'var(--tx)',
          }}
        >
          <div
            style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-base)' }}
          >
            {pdfFiles.length
              ? `${pdfFiles.length} file${pdfFiles.length > 1 ? 's' : ''} selected`
              : 'Click to browse or drag Word files here'}
          </div>
        </button>
        {pdfFiles.length > 0 && (
          <div style={{ maxHeight: rem(100), overflow: 'auto' }}>
            {pdfFiles.map((f, i) => (
              <div
                key={i}
                style={{
                  padding: `${rem(3)} 0`,
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  color: 'var(--tm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                }}
              >
                <span>📄</span>
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {f.split(/[/\\]/).pop()}
                </span>
                <span
                  onClick={() => setPdfFiles((p) => p.filter((_, j) => j !== i))}
                  style={{
                    marginLeft: 'auto',
                    cursor: 'pointer',
                    opacity: 0.4,
                    fontSize: 'var(--font-size-sm)',
                    flexShrink: 0,
                  }}
                >
                  x
                </span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <button
            onClick={pickPdfOutputDir}
            style={{
              padding: `${rem(6)} var(--space-3)`,
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--bd)',
              background: 'var(--ib)',
              color: 'var(--tx)',
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              cursor: 'pointer',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            {pdfOutputDir ? pdfOutputDir : '📁 Output folder (same as source if empty)'}
          </button>
          {pdfOutputDir && (
            <span
              onClick={() => setPdfOutputDir('')}
              style={{ cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}
            >
              x clear
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <button
            onClick={handleConvert}
            disabled={pdfRunning || !pdfFiles.length}
            style={{
              padding: `var(--space-2) ${rem(20)}`,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: pdfRunning || !pdfFiles.length ? 'var(--bd)' : 'var(--ac)',
              color: '#fff',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--font-size-base)',
              cursor: pdfRunning || !pdfFiles.length ? 'not-allowed' : 'pointer',
            }}
          >
            {pdfRunning ? 'Converting...' : 'Convert to PDF'}
          </button>
          <label
            onClick={() => setPdfOpenAfter((p) => !p)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(5),
              cursor: 'pointer',
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              color: 'var(--tm)',
            }}
          >
            <div
              style={{
                width: rem(14),
                height: rem(14),
                borderRadius: rem(3),
                border: `1.5px solid ${pdfOpenAfter ? 'var(--ac)' : 'var(--bd)'}`,
                background: pdfOpenAfter ? 'var(--ac)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                flexShrink: 0,
                transition: 'all .15s',
              }}
            >
              {pdfOpenAfter ? '✓' : ''}
            </div>
            Open folder after
          </label>
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>
          Requires Microsoft Word installed. Uses Word's native PDF export for pixel-perfect output.
        </div>
        {pdfRes && (
          <div>
            {pdfRes.map((r: any, i: number) => (
              <div
                key={i}
                style={{
                  padding: `${rem(5)} 0`,
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(6),
                  borderBottom: '1px solid var(--bd)',
                }}
              >
                <span style={{ fontSize: 'var(--font-size-md)' }}>{r.success ? '✅' : '❌'}</span>
                <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  {(r.outputFile || r.inputFile || '').split(/[/\\]/).pop()}
                </span>
                {!r.success && (
                  <span
                    style={{
                      color: 'var(--dg)',
                      fontSize: 'var(--font-size-sm)',
                      marginLeft: 'auto',
                    }}
                  >
                    {r.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Settings Page ─── */
/* Password, MFA, and connected sign-ins live in the Clerk account portal —
   auth is an on-web process (docs/AUTH_CLERK_PLAN.md); the app never edits
   credentials itself. */
function AccountSecurityCard({ S }: { S: Record<string, React.CSSProperties> }) {
  return (
    <div style={S.card}>
      <div style={S.hdr}>Account & Security</div>
      <div
        style={{
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: rem(10),
        }}
      >
        <div
          style={{
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            color: 'var(--tm)',
            lineHeight: 1.5,
          }}
        >
          Password, sign-in methods, and security settings are managed in your SpecForma account
          portal — it opens in your browser.
        </div>
        <button
          onClick={() => {
            import('../lib/clerkConfig').then(({ CLERK_ACCOUNT_URL }) => {
              window.api?.openExternal?.(CLERK_ACCOUNT_URL);
            });
          }}
          style={{ ...S.btn, alignSelf: 'flex-start' }}
        >
          Manage Account
        </button>
      </div>
    </div>
  );
}

function Settings({
  themeMode,
  onThemeMode,
  standards,
  settings,
  onSettingsChange,
  currentUser,
  userPermissions,
}: {
  themeMode: string;
  onThemeMode: (m: string) => void;
  standards: any[];
  settings: any;
  onSettingsChange: (s: any) => void;
  currentUser: any;
  userPermissions?: string[];
}) {
  const canSetDefaultForma = hasPermission(userPermissions, 'formas.set_default');
  const [newAdmin, setNewAdmin] = eUseState('');
  const [appVersion, setAppVersion] = eUseState('');
  const [aiKeyNew, setAiKeyNew] = eUseState('');
  const [aiHasKey, setAiHasKey] = eUseState(false);
  const [aiMasked, setAiMasked] = eUseState('');
  const [aiKeyLoaded, setAiKeyLoaded] = eUseState(false);
  const [aiKeySaving, setAiKeySaving] = eUseState(false);
  React.useEffect(() => {
    window.api
      .getAppVersion()
      .then((v) => setAppVersion(v))
      .catch(() => {});
  }, []);
  React.useEffect(() => {
    window.api
      .loadApiKey()
      .then((r: any) => {
        setAiHasKey(r?.hasKey || false);
        setAiMasked(r?.masked || '');
        setAiKeyLoaded(true);
      })
      .catch(() => setAiKeyLoaded(true));
  }, []);
  const isAdmin = currentUser && currentUser.role === 'admin';
  const admins: string[] = settings.adminUsernames || [];

  function addAdmin() {
    const u = newAdmin.trim().toLowerCase();
    if (!u) return;
    if (admins.map((a) => a.toLowerCase()).includes(u)) return;
    onSettingsChange({ ...settings, adminUsernames: [...admins, u] });
    setNewAdmin('');
  }
  function removeAdmin(username: string) {
    if (
      username.toLowerCase() === (currentUser.email || currentUser.username || '').toLowerCase()
    ) {
      if (!confirm('Remove yourself from admins? You will lose admin access.')) return;
    }
    onSettingsChange({
      ...settings,
      adminUsernames: admins.filter((a) => a.toLowerCase() !== username.toLowerCase()),
    });
  }
  const S: Record<string, React.CSSProperties> = {
    card: {
      background: 'var(--sf)',
      borderRadius: 10,
      border: '1px solid var(--bd)',
      overflow: 'hidden',
      marginBottom: 12,
    },
    hdr: {
      padding: 'var(--space-3) var(--space-4)',
      borderBottom: '1px solid var(--bd)',
      fontWeight: 'var(--font-weight-semibold)',
      fontSize: 14,
    },
    inp: {
      width: '100%',
      padding: `${rem(6)} ${rem(10)}`,
      borderRadius: 5,
      border: '1px solid var(--bd)',
      background: 'var(--ib)',
      color: 'var(--tx)',
      fontSize: 13,
      boxSizing: 'border-box',
    },
    btn: {
      padding: `${rem(6)} var(--space-4)`,
      borderRadius: 5,
      border: 'none',
      background: 'var(--ac)',
      color: 'var(--color-text-inverse)',
      fontWeight: 'var(--font-weight-semibold)',
      fontSize: 13,
      cursor: 'pointer',
    },
    btnSm: {
      padding: `var(--space-1) ${rem(10)}`,
      borderRadius: 4,
      border: '1px solid var(--bd)',
      background: 'var(--ib)',
      color: 'var(--tx)',
      fontSize: 11,
      cursor: 'pointer',
      fontWeight: 'var(--font-weight-medium)',
    },
  };

  // Interface size (DESIGN.md §3.6) — live effective percent + the user's
  // current factor, so the row can highlight the active option and show a
  // readout that updates on resize or Ctrl+=/Ctrl+-/Ctrl+0 too.
  const [uiScaleV, setUiScaleV] = React.useState(() => getUiScale());
  React.useEffect(() => subscribeUiScale(setUiScaleV), []);
  const interfaceSizeOptions: { label: string; pct: number | null }[] = [
    { label: 'Auto (recommended)', pct: null },
    { label: '90%', pct: 90 },
    { label: '100%', pct: 100 },
    { label: '110%', pct: 110 },
    { label: '125%', pct: 125 },
    { label: '150%', pct: 150 },
  ];

  // Tasks Kit Decision 31: one per-user email switch, read server-side
  // before sending the "assigned to you" email. Own pref, no permission key.
  const [taskAssignedEmail, setTaskAssignedEmail] = usePref<boolean>(
    'notify.taskAssignedEmail',
    'notify.taskAssignedEmail',
    true
  );

  return (
    <div style={{ padding: 'var(--space-5)', maxWidth: rem(760), margin: '0 auto' }}>
      <h2
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          margin: `0 0 ${rem(6)}`,
        }}
      >
        Settings
      </h2>
      <p
        style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)', margin: `0 0 ${rem(20)}` }}
      >
        Signed in as <b>{currentUser.displayName || currentUser.email || currentUser.username}</b> (
        {currentUser.role})
      </p>

      {/* Appearance */}
      <div style={S.card}>
        <div style={S.hdr}>Appearance</div>
        <div
          style={{
            padding: 'var(--space-4)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>Theme</span>
          <div
            style={{
              display: 'flex',
              gap: rem(2),
              background: 'var(--sa)',
              borderRadius: 'var(--radius-md)',
              padding: rem(2),
            }}
          >
            {['light', 'system', 'dark'].map((m) => (
              <Button
                key={m}
                variant={themeMode === m ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => onThemeMode(m)}
                className="capitalize"
                style={{
                  fontSize: 'var(--font-size-base)',
                  boxShadow: themeMode === m ? 'var(--shadow-sm)' : 'none',
                }}
              >
                {m}
              </Button>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: 'var(--space-4)',
            borderTop: '1px solid var(--bd)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--space-2)',
          }}
        >
          <div>
            <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>Interface size</span>
            <span
              style={{
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
                marginLeft: 'var(--space-2)',
              }}
            >
              {formatPercent(uiScaleV.effective)}
            </span>
            {!isWeb && (
              <div
                style={{
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  color: 'var(--tm)',
                  marginTop: rem(2),
                }}
              >
                Ctrl + / Ctrl − also adjust this. Ctrl 0 resets.
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: rem(2),
              background: 'var(--sa)',
              borderRadius: 'var(--radius-md)',
              padding: rem(2),
            }}
          >
            {interfaceSizeOptions.map((opt) => {
              const active =
                opt.pct === null
                  ? uiScaleV.user === 1
                  : Math.round(uiScaleV.effective * 100) === opt.pct;
              return (
                <Button
                  key={opt.label}
                  variant={active ? 'default' : 'ghost'}
                  size="sm"
                  aria-pressed={active}
                  onClick={() =>
                    opt.pct === null
                      ? resetUserScale()
                      : setUserScale(opt.pct / 100 / uiScaleV.dial)
                  }
                  style={{
                    fontSize: 'var(--font-size-base)',
                    boxShadow: active ? 'var(--shadow-sm)' : 'none',
                  }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div style={S.card}>
        <div style={S.hdr}>Notifications</div>
        <div
          style={{
            padding: 'var(--space-4)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}
        >
          <div>
            <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>
              Email me when a task is assigned to me
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', marginTop: rem(2) }}>
              Sent to your sign-in email.
            </div>
          </div>
          <Switch
            checked={taskAssignedEmail}
            onCheckedChange={setTaskAssignedEmail}
            aria-label="Email me when a task is assigned to me"
          />
        </div>
      </div>

      {/* Default Template — overflow visible so the forma dropdown isn't clipped */}
      <div style={{ ...S.card, overflow: 'visible' }}>
        <div style={S.hdr}>Default Template</div>
        <div style={{ padding: 'var(--space-4)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-md)',
              color: 'var(--tm)',
              marginBottom: 'var(--space-2)',
            }}
          >
            Select what template to use as the "New Template" starting point.
          </div>
          <SearchableSelectKV
            value={settings.defaultTemplateId || ''}
            options={standards
              .filter((s) => !isArchived(s) || s.id === settings.defaultTemplateId)
              .map((s) => ({
                value: s.id,
                label: s.docSettings?.standardName || 'Untitled',
              }))}
            onChange={(v) => onSettingsChange({ ...settings, defaultTemplateId: v })}
            placeholder="— None —"
            searchPlaceholder="Search templates…"
            disabled={!canSetDefaultForma}
            style={S.inp}
          />
          {!canSetDefaultForma && (
            <div
              style={{
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
                marginTop: rem(6),
              }}
            >
              You don't have permission to set the default Forma.
            </div>
          )}
        </div>
      </div>

      {/* AI Integration — desktop only: the key is stored and used by the
          local engine; the web target never reads it. */}
      {!isWeb && (
        <div style={S.card}>
          <div style={S.hdr}>AI Integration</div>
          <div style={{ padding: 'var(--space-4)' }}>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--tm)',
                marginBottom: 'var(--space-3)',
                lineHeight: 1.5,
              }}
            >
              Connect an Anthropic API key to enable AI-powered features like spec classification
              and content suggestions.
            </div>
            {!aiKeyLoaded && (
              <div
                aria-hidden="true"
                style={{
                  height: rem(44),
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-3)',
                  background: 'var(--sa)',
                  animation: 'pulse 1.4s ease-in-out infinite',
                }}
              />
            )}
            {aiKeyLoaded && aiHasKey && (
              <div
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(var(--okr),0.06)',
                  border: '1px solid rgba(var(--okr),0.15)',
                  marginBottom: 'var(--space-3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--ok)',
                    }}
                  >
                    API key connected
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      fontFamily: 'var(--font-mono)',
                      marginTop: rem(2),
                    }}
                  >
                    {aiMasked}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    if (!confirm('Remove API key? AI features will be disabled.')) return;
                    setAiKeySaving(true);
                    try {
                      await window.api.saveApiKey('');
                      setAiHasKey(false);
                      setAiMasked('');
                    } catch {}
                    setAiKeySaving(false);
                  }}
                  style={{ ...S.btnSm, color: 'var(--dg)', borderColor: 'var(--dg)' }}
                >
                  Remove
                </button>
              </div>
            )}
            {aiKeyLoaded && !aiHasKey && (
              <div
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'color-mix(in srgb, var(--color-danger) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-danger) 15%, transparent)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--dg)',
                  }}
                >
                  No API key
                </div>
                <div
                  style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', marginTop: rem(2) }}
                >
                  AI features are disabled.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <Input
                value={aiKeyNew}
                onChange={(e) => setAiKeyNew(e.target.value)}
                type="password"
                placeholder={aiHasKey ? 'Enter new key to replace...' : 'sk-ant-api03-...'}
                style={{
                  ...S.inp,
                  flex: 1,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-sm)',
                }}
              />
              <Button
                onClick={async () => {
                  if (!aiKeyNew.trim()) return;
                  setAiKeySaving(true);
                  try {
                    await window.api.saveApiKey(aiKeyNew.trim());
                    const r: any = await window.api.loadApiKey();
                    setAiHasKey(r?.hasKey || false);
                    setAiMasked(r?.masked || '');
                    setAiKeyNew('');
                  } catch {}
                  setAiKeySaving(false);
                }}
                disabled={aiKeySaving || !aiKeyNew.trim()}
                size="sm"
              >
                {aiKeySaving ? 'Saving...' : aiHasKey ? 'Replace' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password */}
      <AccountSecurityCard S={S} />

      {/* Admin Users (hidden) */}
      {false && isAdmin && (
        <div style={S.card}>
          <div style={S.hdr}>
            Admin Users{' '}
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                fontWeight: 'var(--font-weight-regular)',
              }}
            >
              (Admin)
            </span>
          </div>
          <div style={{ padding: 'var(--space-4)' }}>
            <div
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', marginBottom: rem(10) }}
            >
              Windows usernames with admin access. If empty, everyone is admin.
            </div>
            {admins.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: rem(6),
                  marginBottom: 'var(--space-3)',
                }}
              >
                {admins.map((u) => (
                  <div
                    key={u}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      padding: `var(--space-1) ${rem(10)}`,
                      borderRadius: rem(5),
                      background: 'rgba(var(--acr),0.08)',
                      border: '1px solid rgba(var(--acr),0.2)',
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-semibold)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {u}
                    <button
                      type="button"
                      onClick={() => removeAdmin(u)}
                      aria-label={`Remove ${u}`}
                      style={{
                        cursor: 'pointer',
                        opacity: 0.5,
                        marginLeft: rem(2),
                        border: 'none',
                        background: 'transparent',
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 'var(--space-1)',
                      }}
                    >
                      <Icon icon={X} em={0.8462} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: `var(--space-2) ${rem(10)}`,
                  borderRadius: 'var(--radius-md)',
                  background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-warning)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                No admins configured — everyone has admin access.
              </div>
            )}
            <div style={{ display: 'flex', gap: rem(6) }}>
              <Input
                value={newAdmin}
                onChange={(e) => setNewAdmin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addAdmin();
                }}
                placeholder="Windows username (e.g. jsmith)"
                style={{ ...S.inp, flex: 1, fontFamily: 'var(--font-mono)' }}
              />
              <Button onClick={addAdmin} disabled={!newAdmin.trim()} size="sm">
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* About */}
      <div style={S.card}>
        <div style={S.hdr}>About</div>
        <div
          style={{
            padding: 'var(--space-4)',
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
          }}
        >
          SpecForma™ v{appVersion || '...'}
          <br />
          Standardize, organize, and automate specification workflows.
          <br />
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              marginTop: 'var(--space-1)',
              display: 'block',
              opacity: 0.7,
            }}
          >
            Developed by TheBeardedLadyCo™
            <br />
            Copyright © 2026. All rights reserved.
            <br />
          </span>
          {isWeb ? (
            <span style={{ fontSize: 'var(--font-size-sm)', marginTop: rem(10), display: 'block' }}>
              Updates are automatic on the web.
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.api.checkForUpdates()}
              style={{ marginTop: rem(10) }}
            >
              Check for updates
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

(window as any).DocSettings = DocSettings;
(window as any).Library = Library;
(window as any).Formatter = Formatter;
(window as any).Settings = Settings;
export { DocSettings, Library, Formatter, Settings, EditorHistoryPanel };
